import {
  Fragment,
  useEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MutableRefObject,
} from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ChevronRight,
  ChevronsRight,
  Clock,
  Clock3,
  Copy,
  Loader2,
  Lock,
  MessageSquarePlus,
  MessageSquareText,
  Play,
  Plus,
  RotateCw,
  Send,
  Square,
  Trash2,
  Undo2,
  UserCog,
  X,
} from 'lucide-react';
import { Button, Card, ConfirmDialog, InfoHint, SegmentedControl, toast } from '../../components/ui';
import { cn } from '../../lib/cn';
import {
  fetchRecentRows,
  useRecentRowKeys,
  useSetCell,
  useSetCells,
  writtenRows,
  type RemovedEntry,
  type RestoreEntry,
  type RunningTimer,
  type TimeEntry,
  type TimeAgreement,
  type TimeTaskOption,
  type WeekData,
  type WeekRow,
} from './api';
import { EditedByMark, FOREIGN_SHEET_TINT } from './authorship';
import { dayReview, type DayReview } from './timesheet-status';
import { CellNotePopover, type CellNoteTarget } from './CellNotePopover';
import {
  formatCellHours,
  formatElapsed,
  formatHoursValue,
  parseHoursInput,
  todayIso,
  weekdayLabel,
  type HoursFormat,
} from './hours';
import { rowLabel, type RowLabel } from './labels';
import { AgreementTaskPicker, rememberPick, rowKeyOf } from './AgreementTaskPicker';
import { WeekSummary } from './WeekSummary';
import { weekCapacity } from './capacity';

/** Remembered per browser: reviewing notes is a habit, not a one-off. */
const NOTES_KEY = 'financials-time-show-notes';

function readStoredNotes(): boolean {
  // Deep link wins, so a link to a week can carry the notes view with it.
  const param = new URLSearchParams(window.location.search).get('notes');
  if (param === '1' || param === '0') return param === '1';
  try {
    return window.localStorage.getItem(NOTES_KEY) === '1';
  } catch {
    return false;
  }
}

/** "Jun 15" — used in save-failure copy so a toast names the day it lost. */
function shortDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  });
}

/** "Monday, June 15, 2026" — the column header's tooltip, so the compact
 *  "TUE 11" never leaves anyone guessing which date they are typing into. */
function fullDate(iso: string): string {
  return new Date(`${iso}T00:00:00Z`).toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/** A cell whose last save failed: the typed hours are held, not discarded. */
interface FailedCell {
  hours: number;
  message: string;
}

/** Uncommitted hours being typed, so every total can preview the effect. */
interface CellPreview {
  rowKey: string;
  date: string;
  /** Difference from what the server currently holds for that cell. */
  delta: number;
  billable: boolean;
}

// ---------------------------------------------------------------------------
// Cell — a BUTTON until you type into it, then an input that accepts "1:30" and
// "1.5". Arrows always move, Enter/F2/double-click open the editor, Escape
// closes it without writing, Alt+Enter opens the note editor.
// ---------------------------------------------------------------------------

/**
 * Whichever element the cell currently IS. A cell is a button at rest and an
 * input while it is being edited, and `focusCell` has to be able to aim at
 * either — so this is `HTMLElement`, and nothing calls `.select()` on it.
 */
type CellRefs = MutableRefObject<Map<string, HTMLElement>>;

interface CellProps {
  row: number;
  col: number;
  value: number;
  /** Entries behind this cell — how many, and whether any carries a note. */
  entries: TimeEntry[];
  /**
   * An entry here has no description and can still be given one (not billed,
   * not deleted in Harvest, day not under review). Outlined in red: a
   * description is what an invoice line is written from (Jesse, 2026-09-30).
   */
  missingNote?: boolean;
  /**
   * Hours in this cell that cannot be rewritten — billed to an invoice, or
   * deleted in Harvest. Both are a floor the typed total may not go under.
   */
  lockedHours: number;
  fullyLocked: boolean;
  /** Some of those immutable hours were deleted in Harvest, not billed. */
  hasDeletedInSource: boolean;
  /**
   * The REVIEW lock, as the sentence to show — non-null when the whole week is
   * submitted or approved. A third distinct lock from the two above: the
   * billing lock is permanent until the invoice is voided, the Harvest lock is
   * a foreign system's fact, and this one its owner can undo in a click. They
   * must never collapse into one grey "locked".
   */
  reviewLocked: string | null;
  isToday: boolean;
  refs: CellRefs;
  rowCount: number;
  hoursFormat: HoursFormat;
  /** Non-null while the last save for this cell failed. */
  failed: FailedCell | null;
  ariaLabel: string;
  onCommit: (hours: number) => void;
  onRetry: () => void;
  /** Report what is being typed (null = nothing pending) so totals can preview. */
  onPreview: (hours: number | null) => void;
  onOpenNote: (draftHours: number | null, anchor: HTMLElement) => void;
  /** `A` (and ⌘/Ctrl+Enter) from inside the cell — the advertised shortcut. */
  onAddRow: () => void;
  /**
   * `F` — commit this cell and spread the same hours across the rest of the
   * week's weekdays. A letter is never a legal hours value, which is what makes
   * a bare key safe here (same reasoning as `A`).
   */
  /**
   * `F` — spread these hours across the rest of the week. `includeThisCell`
   * says the cell the key was pressed in is part of the SAME write (it was
   * typed and never separately committed), so one Ctrl+Z takes all of it back.
   */
  onFill: (hours: number, includeThisCell?: boolean) => void;
  /** False on the weekend, on Friday, and on a week under review. */
  canFill: boolean;
  /** Escape on a clean cell — hand focus back to the sheet, not nowhere. */
  onLeaveGrid: () => void;
}

/**
 * The note affordance that lives in every cell.
 *
 * Notes are what this app bills and reports on, so a cell must offer one
 * whether or not it already has it: a filled chip when there is something to
 * read, a ghost "+" that surfaces on hover/focus when there is not.
 */
function NoteAffordance({
  entries,
  onOpen,
}: {
  entries: TimeEntry[];
  onOpen: () => void;
}) {
  const notes = entries.map((e) => e.notes).filter((n): n is string => Boolean(n));
  const multi = entries.length > 1;
  const hasNote = notes.length > 0;
  const noteList = notes.map((n) => `“${n}”`).join(', ');
  // ⚠ Somebody other than the owner wrote hours in this cell. It rides on the
  // note chip rather than adding a second glyph — a 64px day column has room
  // for one affordance, and the two facts are read at the same moment anyway.
  const edited = entries.find((e) => e.editedBy)?.editedBy ?? null;
  const editedNote = edited ? ` Changed by ${edited.name}, not by the owner of this timesheet.` : '';
  const title =
    (multi
      ? `${entries.length} entries here${noteList ? ` — ${noteList}` : ''}. Click to edit their hours and notes (Alt+Enter).`
      : hasNote
        ? `${noteList} — click to edit the note (Alt+Enter).`
        : 'Add a note to this time (Alt+Enter)') + editedNote;

  return (
    <button
      type="button"
      // Tab walks day cells only — the note editor is on Alt+Enter.
      tabIndex={-1}
      // Keep focus (and the typed draft) in the cell when this is clicked.
      onMouseDown={(event) => event.preventDefault()}
      onClick={onOpen}
      title={title}
      className={cn(
        'absolute left-0.5 top-1/2 z-10 flex h-4 -translate-y-1/2 items-center gap-0.5 rounded px-1',
        'text-[10px] font-bold leading-none transition-all',
        multi || edited
          ? 'bg-accent/10 text-accent-700 hover:bg-accent/20'
          : hasNote
            ? 'text-grey-400 hover:bg-grey-100 hover:text-accent'
            : 'text-grey-300 opacity-0 hover:bg-grey-100 hover:text-accent focus-visible:opacity-100 group-focus-within/cell:opacity-100 group-hover/cell:opacity-100',
      )}
    >
      {edited && <EditedByMark editedBy={edited} compact />}
      {multi ? (
        <>
          {/* The count is dropped below xl: in a 64px day column it collided
              with the hours it sits beside. The chip's accent tint still says
              "more than one entry", and the tooltip still names them all. */}
          <span className="hidden xl:inline" aria-hidden>
            {entries.length}
          </span>
          <MessageSquareText className="h-2.5 w-2.5" aria-hidden />
          <span className="sr-only">{entries.length} entries — edit hours and notes</span>
        </>
      ) : hasNote ? (
        <>
          <MessageSquareText className="h-2.5 w-2.5" aria-hidden />
          <span className="sr-only">Edit the note</span>
        </>
      ) : (
        <>
          <MessageSquarePlus className="h-2.5 w-2.5" aria-hidden />
          <span className="sr-only">Add a note</span>
        </>
      )}
    </button>
  );
}

/**
 * An entry that has no description and can still be given one — the one test
 * behind the red outlines and the "N missing" count. The day's review lock is
 * checked by the caller (it lives on the week, not the entry).
 */
function needsNote(entry: TimeEntry): boolean {
  return !entry.notes?.trim() && !entry.locked && !entry.deletedInSource;
}

function CellInput({
  row,
  col,
  value,
  entries,
  missingNote = false,
  lockedHours,
  fullyLocked,
  hasDeletedInSource,
  reviewLocked,
  isToday,
  refs,
  rowCount,
  hoursFormat,
  failed,
  ariaLabel,
  onCommit,
  onRetry,
  onPreview,
  onOpenNote,
  onAddRow,
  onFill,
  canFill,
  onLeaveGrid,
}: CellProps) {
  const [draft, setDraft] = useState(() => formatCellHours(value, hoursFormat));
  const [error, setError] = useState<string | null>(null);
  /**
   * ⚠ **A CELL IS A BUTTON UNTIL YOU ASK FOR AN EDITOR** — the same rule the
   * money grids follow (`features/cashflow/grid/Sheet.tsx`), arrived at here
   * for a worse reason.
   *
   * Every cell used to be a live `<input>`, and every focus SELECTED its
   * contents. Two things followed, on the screen with ten times the keystrokes
   * of the money grids, on hours that become invoice lines:
   *
   *   1. One keystroke destroyed real work. Landing on a populated cell put
   *      "2.50" in the field, fully selected; typing `9` and clicking away
   *      wrote 9.00, and pressing only Backspace and clicking away CLEARED the
   *      cell — which hard-deletes the entry, its note and its id. No dialog,
   *      no toast naming it, nothing to press to get it back.
   *   2. ArrowLeft was a dead end in any populated cell. Arrows inside an input
   *      move the CARET, so the cell only released focus once the caret reached
   *      the edge — and since focus arrived with everything selected, going
   *      left just collapsed the selection and stayed. Arrow navigation worked
   *      on empty cells, which taught the model, then inverted on the cells
   *      holding data.
   *
   * So: a click SELECTS. Typing a digit opens the editor already holding that
   * character (it costs no extra step — Jesse's rule), and Enter, F2 or a
   * second click open it on the current value. Arrows always move, because a
   * button has no caret to trap them. Escape closes the editor without writing.
   */
  const [editing, setEditing] = useState(false);
  /** True while the *input* has focus (not the button) — see the sync effect. */
  const focused = useRef(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement | null>(null);
  /** Was the button already focused when this click started? (click 2 opens.) */
  const wasFocused = useRef(false);
  /** The editor was opened by typing — put the caret after the seed, not over it. */
  const seeded = useRef(false);
  /**
   * Blur guard. Focus leaving for the note popover must not write the draft the
   * popover is about to take over (and Escape must never write at all). A ref,
   * not state, because blur runs in the same tick.
   */
  const cancelling = useRef(false);
  // Last value this cell sent to the server — guards against Enter + the blur
  // it triggers double-committing the same total (two racing creates).
  const committed = useRef(Math.round(value * 100) / 100);

  // Track server value while the cell is not being edited. A failed save is the
  // exception: what the user typed stays on screen until it saves or is undone.
  useEffect(() => {
    committed.current = Math.round(value * 100) / 100;
    // `error` is the second exception: a blurred-but-invalid draft is held on
    // screen, so a background refetch must not quietly wipe what was typed.
    if (!focused.current && !failed && !error) {
      setDraft(formatCellHours(value, hoursFormat));
    }
  }, [value, hoursFormat, failed, error]);

  useEffect(() => {
    if (!failed) return;
    // Re-arm: the server never took these hours, so committing them again must
    // actually issue a write.
    committed.current = Math.round(value * 100) / 100;
    if (!focused.current) setDraft(formatCellHours(failed.hours, hoursFormat));
  }, [failed, hoursFormat, value]);

  /** Revert the cell to the server value and clear any validation error. */
  const reset = () => {
    setDraft(formatCellHours(value, hoursFormat));
    setError(null);
    onPreview(null);
  };

  /**
   * Check the draft WITHOUT writing it: the hours, or the reason they are
   * refused. `F` needs this separately from `commit`, because the cell it was
   * pressed in is written by the fill's batch and must not also be written on
   * its own — but it still has to be validated first, or an impossible value
   * (99, or less than the billed floor) would be spread across the week.
   */
  const validateDraft = (): number | string => {
    const parsed = parseHoursInput(draft);
    if (parsed === null) {
      const message = 'Enter hours as 1.5 or 1:30';
      setError(message);
      return message;
    }
    if (parsed > 24) {
      const message = 'A day holds at most 24h';
      setError(message);
      return message;
    }
    if (parsed < lockedHours) {
      const message = hasDeletedInSource
        ? `${formatHoursValue(lockedHours, hoursFormat)} here was deleted in Harvest — can't go lower`
        : `${formatHoursValue(lockedHours, hoursFormat)} here is billed — can't go lower`;
      setError(message);
      return message;
    }
    setError(null);
    return parsed;
  };

  /** Commit the draft. Returns the reason it could not, or null on success. */
  const commit = (): string | null => {
    const checked = validateDraft();
    if (typeof checked === 'string') return checked;
    setDraft(formatCellHours(checked, hoursFormat));
    onPreview(null);
    if (checked !== committed.current) {
      committed.current = checked;
      onCommit(checked);
    }
    return null;
  };

  const focusCell = (r: number, c: number) => {
    refs.current.get(`${r}:${c}`)?.focus();
  };

  /**
   * Open the editor. `seed` is the character that opened it (typing), or
   * undefined to edit what is already there with it selected.
   */
  const openEditor = (seed?: string) => {
    seeded.current = seed !== undefined;
    setDraft(seed ?? formatCellHours(value, hoursFormat));
    if (seed !== undefined) {
      const parsed = parseHoursInput(seed);
      onPreview(parsed !== null && parsed <= 24 && parsed >= lockedHours ? parsed : null);
    }
    setEditing(true);
  };

  /**
   * Leave the editor; the cell goes back to being a button.
   *
   * ⚠ `focused` is cleared HERE, because the input is unmounting and React
   * fires no blur on unmount: leaving the flag set told the sync effect the
   * user was still typing, so the cell kept showing its old draft while the
   * row and week totals moved underneath it. After `F` + Ctrl+Z that meant a
   * cell reading `8.00` inside a row totalling `3.00` — a figure the database
   * did not hold.
   */
  const closeEditor = (refocus = true) => {
    focused.current = false;
    setEditing(false);
    if (refocus) requestAnimationFrame(() => buttonRef.current?.focus());
  };

  /** Hand the cell's current draft to the note popover without writing it. */
  const openNote = (fromInput: boolean) => {
    if (fromInput) cancelling.current = true;
    onPreview(null);
    if (wrapRef.current) onOpenNote(parseHoursInput(draft), wrapRef.current);
  };

  /**
   * `A` adds a row and `F` fills the week — from the button and from the
   * editor, so the advertised shortcuts work wherever the cursor actually is.
   * Safe as bare keys in the editor too: a letter is never a legal hours value
   * (`parseHoursInput` rejects every one), so nothing can be lost to them.
   */
  const claimedShortcut = (event: KeyboardEvent<HTMLElement>, fill: () => void): boolean => {
    if (event.metaKey || event.ctrlKey || event.altKey) return false;
    if (event.key === 'a' || event.key === 'A') {
      event.preventDefault();
      onAddRow();
      return true;
    }
    if (event.key === 'f' || event.key === 'F') {
      event.preventDefault();
      fill();
      return true;
    }
    return false;
  };

  /**
   * ⚠ **`F` FROM THE EDITOR IS ONE WRITE, NOT TWO.**
   *
   * It used to fire the fill and THEN commit the typed cell, so the grid
   * recorded the fill and then overwrote that record with the single cell —
   * and Ctrl+Z put one cell back while four filled cells stayed in the billing
   * database with nothing on screen offering to undo them. The typed cell is
   * now part of the fill's own batch: validated here, written there, undone
   * with it. `committed.current` is set first so the blur this triggers cannot
   * write the same cell a second time.
   */
  const fillFromEditor = () => {
    if (!canFill) return;
    const checked = validateDraft();
    if (typeof checked === 'string' || checked <= 0) return;
    committed.current = checked;
    setDraft(formatCellHours(checked, hoursFormat));
    onPreview(null);
    closeEditor();
    onFill(checked, true);
  };

  const onButtonKeyDown = (event: KeyboardEvent<HTMLButtonElement>) => {
    // From the button the cell already holds what it will spread, so the fill
    // writes the OTHER days only.
    if (
      claimedShortcut(event, () => {
        if (canFill && value > 0) onFill(value);
      })
    ) {
      return;
    }
    if (event.key === 'Enter' && (event.altKey || event.shiftKey)) {
      event.preventDefault();
      openNote(false);
      return;
    }
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
      onAddRow();
      return;
    }
    if (event.key === 'Enter' || event.key === 'F2') {
      event.preventDefault();
      openEditor();
      return;
    }
    if (event.key === 'ArrowUp') {
      event.preventDefault();
      focusCell(Math.max(row - 1, 0), col);
      return;
    }
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      focusCell(Math.min(row + 1, rowCount - 1), col);
      return;
    }
    // ⚠ The whole point: from a BUTTON these always move, populated or not.
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      focusCell(row, Math.max(col - 1, 0));
      return;
    }
    if (event.key === 'ArrowRight') {
      event.preventDefault();
      focusCell(row, Math.min(col + 1, 6));
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      onLeaveGrid();
      return;
    }
    /**
     * Delete/Backspace clears the cell — the spreadsheet gesture — but ONLY
     * where nothing but a number is lost. A cell holding several entries, or a
     * note, is somebody's description of billable work: clearing it is a
     * deletion with no keystroke behind it, so it is refused with the way in.
     * What it does write is tracked, announced and undoable like any other cell
     * edit (Ctrl+Z).
     */
    if (event.key === 'Backspace' || event.key === 'Delete') {
      event.preventDefault();
      if (value <= 0) return;
      if (entries.length > 1 || entries.some((e) => e.notes)) {
        toast.info(
          entries.length > 1
            ? `${entries.length} entries are logged here — open the cell (Alt+Enter) to change them.`
            : 'This time has a description — open the cell (Alt+Enter) to change it.',
        );
        return;
      }
      setDraft(formatCellHours(0, hoursFormat));
      setError(null);
      if (committed.current !== 0) {
        committed.current = 0;
        onCommit(0);
      }
      return;
    }
    // A digit (or a separator) starts an edit holding what was typed — typing
    // into a cell must never cost an extra keystroke to "open" it first.
    if (event.key.length === 1 && /[0-9.:]/.test(event.key)) {
      event.preventDefault();
      openEditor(event.key);
    }
  };

  const onInputKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    const input = event.currentTarget;
    const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
    const atEnd =
      input.selectionStart === input.value.length && input.selectionEnd === input.value.length;
    if (claimedShortcut(event, fillFromEditor)) return;
    if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      // The documented "next row, please" exit: bank what is typed, then pick
      // the next agreement/task.
      event.preventDefault();
      commit();
      closeEditor(false);
      onAddRow();
      return;
    }
    if (event.key === 'Enter' && (event.altKey || event.shiftKey)) {
      event.preventDefault();
      openNote(true);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      if (commit() === null) {
        setEditing(false);
        const next = Math.min(row + 1, rowCount - 1);
        requestAnimationFrame(() => focusCell(next, col));
      }
    } else if (event.key === 'Escape') {
      // ESCAPE NEVER WRITES. `cancelling` is set BEFORE the state change,
      // because the blur fired by the unmounting input would otherwise commit
      // the draft this keystroke exists to throw away.
      event.preventDefault();
      cancelling.current = true;
      reset();
      closeEditor();
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (commit() === null) {
        setEditing(false);
        const next = Math.max(row - 1, 0);
        requestAnimationFrame(() => focusCell(next, col));
      }
    } else if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (commit() === null) {
        setEditing(false);
        const next = Math.min(row + 1, rowCount - 1);
        requestAnimationFrame(() => focusCell(next, col));
      }
    } else if (event.key === 'ArrowLeft' && atStart) {
      event.preventDefault();
      if (commit() === null) {
        setEditing(false);
        const next = Math.max(col - 1, 0);
        requestAnimationFrame(() => focusCell(row, next));
      }
    } else if (event.key === 'ArrowRight' && atEnd) {
      event.preventDefault();
      if (commit() === null) {
        setEditing(false);
        const next = Math.min(col + 1, 6);
        requestAnimationFrame(() => focusCell(row, next));
      }
    }
  };

  const retryButton = failed ? (
    <button
      type="button"
      tabIndex={-1}
      onMouseDown={(event) => event.preventDefault()}
      onClick={onRetry}
      title={`Not saved — ${failed.message} Click to retry; your ${formatHoursValue(
        failed.hours,
        hoursFormat,
      )} is still here.`}
      className="absolute left-0.5 top-1/2 z-10 flex h-4 -translate-y-1/2 items-center gap-0.5 rounded bg-warning/15 px-1 text-[10px] font-bold leading-none text-status-partial transition-colors hover:bg-warning/30"
    >
      <AlertTriangle className="h-2.5 w-2.5" aria-hidden />
      <RotateCw className="h-2.5 w-2.5" aria-hidden />
      <span className="sr-only">Not saved — retry</span>
    </button>
  ) : null;

  if (fullyLocked) {
    return (
      <div ref={wrapRef} className="relative">
        <div
          className={cn(
            'flex h-8 items-center justify-end gap-1 rounded-md px-2 text-sm num',
            hasDeletedInSource
              ? 'bg-warning/10 text-status-partial line-through decoration-status-partial/60'
              : 'bg-grey-100/70 text-grey-500',
          )}
          title={
            hasDeletedInSource
              ? 'Deleted in Harvest — kept here as history. Not editable, and never billed.'
              : 'Billed on an invoice — locked'
          }
        >
          {hasDeletedInSource ? (
            <AlertTriangle className="h-3 w-3 shrink-0" aria-hidden />
          ) : (
            <Lock className="h-3 w-3 shrink-0" aria-hidden />
          )}
          {formatCellHours(value, hoursFormat) || '0'}
        </div>
        {entries.some((e) => e.notes) && (
          <NoteAffordance entries={entries} onOpen={() => onOpenNote(null, wrapRef.current!)} />
        )}
      </div>
    );
  }

  /**
   * A week under review reads, it does not type.
   *
   * Deliberately NOT a disabled `<input>`: a greyed-out box that still looks
   * like a field invites a click and answers with nothing. This is a figure on
   * the page — plain, aligned with the editable cells above and below it, and
   * carrying the reason in its tooltip. Empty cells print an em dash instead of
   * a blank so the sheet still reads as a completed record.
   */
  if (reviewLocked) {
    return (
      <div ref={wrapRef} className="relative">
        <div
          className="flex h-8 items-center justify-end rounded-md px-2 text-sm text-secondary num"
          title={reviewLocked}
        >
          {formatCellHours(value, hoursFormat) || <span className="text-grey-300">—</span>}
        </div>
        {/* Static, not a button: the note editor writes, and this week does
            not take writes. The descriptions are still readable — in the
            tooltip, and in full under each row via the Notes toggle. */}
        {entries.some((e) => e.notes) && (
          <span
            className="absolute left-0.5 top-1/2 flex h-4 -translate-y-1/2 items-center rounded px-1 text-grey-400"
            title={entries
              .map((e) => e.notes)
              .filter(Boolean)
              .map((n) => `“${n}”`)
              .join(', ')}
          >
            <MessageSquareText className="h-2.5 w-2.5" aria-hidden />
            <span className="sr-only">This entry has a description</span>
          </span>
        )}
      </div>
    );
  }

  const shell = cn(
    'h-8 w-full rounded-md border bg-transparent px-1.5 text-right text-sm text-secondary num',
    'transition-colors focus:outline-none focus:ring-2',
    error
      ? 'border-error bg-error/5 focus:border-error focus:ring-error/20'
      : failed
        ? 'border-warning bg-warning/10 focus:border-warning focus:ring-warning/25'
        : missingNote
          ? cn(
              'border-error/70 bg-error/[0.03] focus:border-error focus:bg-white focus:ring-error/20',
              isToday && 'bg-accent/[0.04]',
            )
          : cn(
            // A typical week draws 42 of these boxes for about ten real
            // figures, and the outlines carried more weight than the numbers.
            // Cells that hold something keep a hairline; empty ones recede to
            // nothing and come back the instant the row is hovered (or the cell
            // focused), so "row 4, Thursday" is still findable without 42
            // permanent rectangles on screen.
            // ⚠ AN EMPTY CELL MUST STILL SAY "TYPE HERE". Fully transparent,
            // a half-filled sheet gave no hint that Wed–Sun took input at all
            // until the pointer happened to cross them — and a keyboard user
            // never saw one. A dashed baseline is a fill-in-the-blank rule: it
            // reads as an invitation without putting 42 rectangles back on the
            // screen, and it is not the read-only week's em dash.
            draft
              ? 'border-grey-200/70'
              : 'border-transparent border-b-grey-200 [border-bottom-style:dashed] group-hover:border-grey-200 group-hover:[border-bottom-style:solid]',
            'focus:border-accent focus:bg-white focus:ring-accent/20',
            isToday && 'bg-accent/[0.04]',
          ),
  );

  const lockedHint =
    lockedHours > 0
      ? hasDeletedInSource
        ? `${formatHoursValue(lockedHours, hoursFormat)} h in this cell were deleted in Harvest and are kept as history`
        : `${formatHoursValue(lockedHours, hoursFormat)} h in this cell are billed and locked`
      : undefined;

  return (
    <div ref={wrapRef} className="relative">
      {editing ? (
        <input
          ref={(el) => {
            if (el) refs.current.set(`${row}:${col}`, el);
            else refs.current.delete(`${row}:${col}`);
          }}
          autoFocus
          inputMode="decimal"
          value={draft}
          placeholder=""
          // Marks this as the grid's own numeric cell, so the screen-level
          // shortcut listener can exempt it from its "the user is typing" guard.
          data-grid-cell="hours"
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          title={failed ? `Not saved yet — ${failed.message}` : lockedHint}
          onFocus={(e) => {
            focused.current = true;
            // Opened by typing: the caret goes AFTER the character, so the
            // seed is not the first thing the next keystroke replaces.
            if (seeded.current) {
              const end = e.currentTarget.value.length;
              e.currentTarget.setSelectionRange(end, end);
            } else {
              e.currentTarget.select();
            }
          }}
          onChange={(e) => {
            setDraft(e.target.value);
            setError(null);
            const parsed = parseHoursInput(e.target.value);
            onPreview(parsed !== null && parsed <= 24 && parsed >= lockedHours ? parsed : null);
          }}
          onBlur={() => {
            focused.current = false;
            setEditing(false);
            // Escape, or the note popover taking the draft — never write.
            if (cancelling.current) {
              cancelling.current = false;
              reset();
              return;
            }
            // Typed hours are never thrown away on the way out. Whatever could
            // not be parsed or saved stays in the cell, red, with the reason —
            // silently reverting to blank is keystroke loss in an entry grid.
            const reason = commit();
            if (reason) {
              toast.error(
                `“${draft.trim()}” isn't hours — ${reason}. It is still in the cell; press Escape there to discard it.`,
              );
            }
          }}
          onKeyDown={onInputKeyDown}
          className={shell}
        />
      ) : (
        <button
          ref={(el) => {
            buttonRef.current = el;
            if (el) refs.current.set(`${row}:${col}`, el);
            else refs.current.delete(`${row}:${col}`);
          }}
          type="button"
          data-grid-cell="hours"
          aria-label={ariaLabel}
          aria-invalid={error ? true : undefined}
          title={
            failed
              ? `Not saved yet — ${failed.message}`
              : (missingNote ? 'An entry here has no description — Alt+Enter to add one. ' : '') +
                (lockedHint ? `${lockedHint}. ` : '') +
                'Type to enter hours · Enter or double-click to edit · arrows move'
          }
          onMouseDown={() => {
            wasFocused.current = document.activeElement === buttonRef.current;
          }}
          onClick={() => {
            // First click SELECTS the cell (so the arrow keys work from where
            // the mouse landed); a second click opens the editor.
            if (wasFocused.current) openEditor();
          }}
          onDoubleClick={() => openEditor()}
          onKeyDown={onButtonKeyDown}
          className={cn(shell, 'flex items-center justify-end')}
        >
          {draft || <span className="sr-only">empty</span>}
        </button>
      )}
      {retryButton ?? <NoteAffordance entries={entries} onOpen={() => openNote(editing)} />}
      {/* ⚠ IT MUST NOT COVER A FIGURE IN ANOTHER ROW. Drawn below the cell,
          this sat squarely on the next row's hours — the reader was told "A day
          holds at most 24h" over a hidden 8.75. Beside the cell it can only
          ever cover cells in the row being edited, which come back the moment
          the error clears, and it flips at the right-hand columns so it never
          pushes the table sideways. */}
      {error && (
        <span
          role="alert"
          className={cn(
            'pointer-events-none absolute top-1/2 z-40 w-max max-w-[10rem] -translate-y-1/2 rounded-md bg-secondary px-2 py-1 text-caption font-semibold leading-tight text-white shadow-elevated',
            col >= 4 ? 'right-full mr-1' : 'left-full ml-1',
          )}
        >
          {error}
        </span>
      )}
    </div>
  );
}

/**
 * Cell for the day a timer is running on: read-only, ticking, and clickable to
 * stop — the running seconds are not saved on the entry until the timer stops.
 */
function RunningCell({
  savedHours,
  elapsedMs,
  onStop,
  disabled,
}: {
  /** Hours already committed to this cell (the live clock counts on top). */
  savedHours: number;
  elapsedMs: number;
  onStop: () => void;
  disabled: boolean;
}) {
  const totalMs = savedHours * 3_600_000 + elapsedMs;
  return (
    <button
      type="button"
      onClick={onStop}
      disabled={disabled}
      title={`Timer running — ${formatElapsed(elapsedMs)} so far. Click to stop.`}
      className="flex h-8 w-full items-center justify-end gap-1 rounded-md border border-primary/30 bg-primary/5 px-1.5 text-xs font-bold text-primary transition-colors hover:bg-primary/10 num"
    >
      <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden>
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
      </span>
      {formatElapsed(totalMs)}
      <span className="sr-only">Stop timer</span>
    </button>
  );
}

/**
 * Which part of `client › agreement › task` actually tells this row from its
 * neighbours. Emphasis follows it, so the loud text on every row is the text
 * that is doing work — a sheet with two bold "Hosting & Maintenance" rows,
 * separated only by a small grey client line, is a sheet you have to read
 * twice.
 */
interface RowEmphasis {
  /** The client line (line 1) carries the weight. Decided for the WHOLE sheet. */
  client: boolean;
  /** The agreement name (line 2) carries it. */
  agreement: boolean;
  /** The task chip (line 2) carries it. */
  task: boolean;
}

/**
 * ⚠ **WHICH LINE is loud is a SHEET-WIDE decision; which TOKEN on line 2 is
 * loud stays per row.**
 *
 * The first version decided everything per row, and the result read like a
 * rendering bug: in an 8-row week the bold token sat on line 1 for rows 1 and 5
 * (two different clients both running "Hosting & Maintenance") and on line 2
 * for the other six, so scanning the identity column the weight jumped up and
 * down the rows. The intent — put the weight on the text that is doing work —
 * is right; applying it row by row is what broke it.
 *
 * So: if ANY agreement name in this week appears under more than one client, the
 * client line leads on EVERY row (one straight column of bold, and the token
 * that disambiguates is always the one carrying it). Otherwise the agreement name
 * leads on every row. Only the second line's agreement-vs-task choice varies, and
 * both of those sit on the same line, so nothing moves vertically.
 */
function rowEmphases(labels: RowLabel[]): RowEmphasis[] {
  const clientsPerAgreement = new Map<string, Set<string>>();
  const byPair = new Map<string, number>();
  for (const label of labels) {
    const clients = clientsPerAgreement.get(label.agreement) ?? new Set<string>();
    clients.add(label.client);
    clientsPerAgreement.set(label.agreement, clients);
    const pair = `${label.client} — ${label.agreement}`;
    byPair.set(pair, (byPair.get(pair) ?? 0) + 1);
  }
  // One agreement name under two clients anywhere in the sheet → the client is
  // load-bearing, for every row, not just the two that collide.
  const clientLeads = [...clientsPerAgreement.values()].some((clients) => clients.size > 1);
  return labels.map((label) => {
    // Same client AND agreement on two rows → only the task tells them apart.
    const taskLeads = (byPair.get(`${label.client} — ${label.agreement}`) ?? 0) > 1;
    return {
      client: clientLeads,
      agreement: !clientLeads && !taskLeads,
      task: taskLeads,
    };
  });
}

/**
 * Row identity: client on top (Harvest's order), agreement + task beneath, with
 * the discriminating field carrying the weight and the shared context receding.
 */
function RowIdentity({
  label,
  billable,
  emphasis,
}: {
  label: RowLabel;
  billable: boolean;
  emphasis: RowEmphasis;
}) {
  return (
    <div className="min-w-0 flex-1" title={label.full}>
      {/* The client leads. "Copper Canyon Outfitters" and "Pemberton Realty"
          were once the only thing telling two rows apart and both sat last, in
          small grey type — so when the agreement repeats, this line takes the
          weight instead of merely carrying it. */}
      <p
        className={cn(
          'flex items-center gap-1.5 leading-tight',
          emphasis.client
            ? 'text-sm font-bold text-secondary'
            : 'text-caption text-grey-500',
        )}
      >
        {/* Always present, always the same x: the dot used to slide to wherever
            the row's text happened to end, so a column of them read as noise.
            Green/grey matches the Billable / Internal legend in the summary. */}
        <span
          className={cn(
            'h-1.5 w-1.5 shrink-0 rounded-full',
            billable ? 'bg-success' : 'bg-grey-300',
          )}
          title={billable ? 'Billable' : 'Internal (not billable)'}
        />
        {/* Truncation is only dangerous on the field that tells rows apart:
            "Hosting & Mai…" twice is an unreadable sheet, but a clipped line
            of shared context costs nothing. So the discriminator wraps to a
            second line at 390px and everything else is allowed to clip —
            which is what keeps a phone row two lines instead of five. */}
        <span
          className={cn(
            'min-w-0',
            emphasis.client
              ? 'line-clamp-2 [overflow-wrap:anywhere] sm:line-clamp-none sm:truncate'
              : 'truncate',
          )}
        >
          {label.client}
        </span>
      </p>
      <p className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5 pl-3 leading-tight sm:flex-nowrap">
        <span
          className={cn(
            'min-w-0',
            emphasis.agreement
              ? 'line-clamp-2 text-sm font-bold text-secondary [overflow-wrap:anywhere] sm:line-clamp-none sm:truncate'
              : 'truncate text-[13px] font-semibold text-grey-600',
          )}
        >
          {label.agreement}
        </span>
        {label.task && (
          // The task is often the ONLY thing separating two rows of the same
          // agreement — it must never be the part that gets ellipsised away, and
          // when it IS the differentiator it stops being a grey afterthought.
          <span
            title={
              emphasis.task
                ? 'This task is what separates this row from the others on the same agreement'
                : undefined
            }
            className={cn(
              'max-w-full rounded px-1.5 py-px text-[11px]',
              emphasis.task
                ? // The one token separating this row from its neighbour is
                  // never the one that gets ellipsised away.
                  'bg-accent/10 font-bold text-accent-700 [overflow-wrap:anywhere] sm:max-w-[60%] sm:shrink-0 sm:truncate'
                : 'shrink-0 truncate bg-grey-100 font-semibold text-grey-600 sm:max-w-[52%]',
            )}
          >
            {label.task}
          </span>
        )}
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Week grid
// ---------------------------------------------------------------------------

export interface WeekGridProps {
  week: WeekData;
  weekStart: string;
  personId: string | null;
  agreements: TimeAgreement[];
  runningTimer: RunningTimer | null;
  timerBusy: boolean;
  hoursFormat: HoursFormat;
  onHoursFormatChange: (format: HoursFormat) => void;
  onStartTimer: (agreementId: string, taskId: string | null) => void;
  onStopTimer: () => void;
  /** Switch the page to the day view for a date (per-entry editing). */
  onOpenDay: (date: string) => void;
  /**
   * Filled with "put the cursor where hours are typed" so the page's
   * "Skip to timesheet" link lands in the grid rather than merely near it.
   */
  focusRef?: MutableRefObject<(() => boolean) | null>;
  /**
   * ⚠ This is somebody ELSE's timesheet (an approver reviewing a colleague).
   *
   * The grid stays writable — reviewing and fixing a week is what an approver
   * is for — but it must not look like your own sheet: it wears a tint, it
   * refuses to START a clock on another person's day (stopping a running one
   * is still offered, because that is a correction rather than an invention),
   * and every write it makes is recorded server-side against the actor's name.
   */
  foreignSheet?: boolean;
  /**
   * The viewer is a financial ADMIN: a SUBMITTED period stays writable for
   * them (the admin correction). The server enforces it; this only decides
   * whether the cells render as inputs or as figures.
   */
  isAdmin?: boolean;
}

/** Locally-added (still empty) rows: agreement × task picked but no hours yet. */
interface LocalRow {
  agreementId: string;
  taskId: string | null;
  taskName: string | null;
  billable: boolean;
}

interface GridRow {
  key: string;
  agreementId: string;
  taskId: string | null;
  agreement: TimeAgreement;
  taskName: string | null;
  billable: boolean;
  serverRow: WeekRow | null;
}

export function WeekGrid({
  week,
  weekStart,
  personId,
  agreements,
  runningTimer,
  timerBusy,
  hoursFormat,
  onHoursFormatChange,
  onStartTimer,
  onStopTimer,
  onOpenDay,
  focusRef,
  foreignSheet = false,
  isAdmin = false,
}: WeekGridProps) {
  const [localRows, setLocalRows] = useState<LocalRow[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [rowToDelete, setRowToDelete] = useState<GridRow | null>(null);
  const [copyState, setCopyState] = useState<'idle' | 'loading' | 'none'>('idle');
  const [hoverCol, setHoverCol] = useState<number | null>(null);
  const [failedCells, setFailedCells] = useState<Map<string, FailedCell>>(() => new Map());
  const [preview, setPreview] = useState<CellPreview | null>(null);
  const [noteTarget, setNoteTarget] = useState<CellNoteTarget | null>(null);
  const [showNotes, setShowNotes] = useState(readStoredNotes);
  /**
   * Horizontal-overflow state of the day columns.
   *
   * The table has a 700px floor, so below ~900px Sunday (and often Wednesday)
   * sits outside the viewport. It used to clip mid-glyph at the edge with no
   * cue at all, which reads as a rendering bug rather than "there is more
   * week over there". Tracked rather than an always-on gradient: a permanent
   * fade at the end of the scroll is its own small lie.
   */
  const scrollRef = useRef<HTMLDivElement>(null);
  /** Focus parking spot for Escape — see `leaveGrid`. */
  const sheetRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState({ left: false, right: false });
  const noteTargetRef = useRef<CellNoteTarget | null>(null);
  const cellRefs = useRef(new Map<string, HTMLElement>());
  const addRowRef = useRef<HTMLButtonElement>(null);
  /** The empty week's own primary button — the keyboard's landing spot there. */
  const emptyAddRef = useRef<HTMLButtonElement>(null);
  /**
   * Row index whose cell should take focus once the grid has re-rendered.
   *
   * "Add an agreement, then log hours to it" is the most frequent compound action
   * on this screen. Adding a row used to drop focus on `<body>`, so the flow
   * broke at its exact midpoint and the next keystroke went nowhere. A new row
   * is appended last, so its index is the row count taken BEFORE the append.
   */
  const pendingFocusRow = useRef<number | null>(null);
  /** Key of the row just added — tinted briefly so the eye finds the cursor. */
  const [newRowKey, setNewRowKey] = useState<string | null>(null);
  /**
   * Pinned display order.
   *
   * The API returns rows sorted by client/agreement/task, so a row added at the
   * bottom used to JUMP to its alphabetical position the instant its first
   * hours committed — the row moved out from under the cursor mid-edit, which
   * in a spreadsheet is unforgivable. A key's position is captured the first
   * time it is seen and held for the life of this grid; the grid is re-keyed
   * per person+week, so changing week or reloading re-sorts properly.
   */
  const orderRef = useRef<{ next: number; index: Map<string, number> }>({
    next: 0,
    index: new Map(),
  });

  /**
   * The week's REVIEW lock, as the sentence every read-only affordance shows.
   *
   * Held as a string rather than a boolean so the cells, the buttons and the
   * footer all say the same thing, and so the reason ("submitted" vs
   * "approved") travels with the state instead of being re-derived three times.
   */
  /**
   * ⚠ THE REVIEW LOCK'S GLYPH IS THE WEEK'S OWN STATUS GLYPH, never `Lock`.
   * `Lock` belongs to the BILLING lock alone (see `timesheet-status.tsx` for
   * the same two icons on the status pill).
   */
  /** Any hour on this week already sits on an invoice — the legend needs it. */
  const hasBilledHours = week.rows.some((row) => row.entries.some((entry) => entry.locked));

  /**
   * ⚠ THE REVIEW LOCK IS PER DAY. A timesheet is a period (a day, a week or a
   * month), so the week on screen can straddle two: Aug 31 in an approved
   * August and Sep 1–6 in an open September. Each day takes its lock from the
   * period that governs it (`dayStatus`), never from the week as a whole.
   */
  const reviews = useMemo(
    () => new Map(week.days.map((d) => [d, dayReview(week, d, isAdmin)])),
    [week, isAdmin],
  );
  const lockOf = (date: string): string | null => reviews.get(date)?.lock ?? null;
  const lockedDays = week.days.filter((d) => lockOf(d) !== null);
  /** Every day is closed — the whole-sheet affordances go (add row, copy, keys). */
  const reviewLocked =
    week.days.length > 0 && lockedDays.length === week.days.length ? lockOf(week.days[0]) : null;
  /** The closed periods and the days of this week each one holds. */
  const lockedPeriods = (() => {
    const byPeriod = new Map<string, { review: DayReview; days: string[] }>();
    for (const d of lockedDays) {
      const review = reviews.get(d)!;
      const key = `${review.periodKind}:${review.periodStart}`;
      const hit = byPeriod.get(key);
      if (hit) hit.days.push(d);
      else byPeriod.set(key, { review, days: [d] });
    }
    return [...byPeriod.values()];
  })();
  /** An admin writing into a submitted period — a correction, and it looks like one. */
  const correctingDays = week.days.filter((d) => reviews.get(d)?.correcting);
  const ReviewLockIcon =
    reviews.get(lockedDays[0] ?? week.days[0])?.status === 'APPROVED' ? CheckCircle2 : Send;

  const setCell = useSetCell(weekStart, personId);
  const setCells = useSetCells(weekStart, personId);

  useEffect(() => {
    try {
      window.localStorage.setItem(NOTES_KEY, showNotes ? '1' : '0');
    } catch {
      /* private mode — the toggle just doesn't persist */
    }
  }, [showNotes]);

  const today = todayIso();
  const agreementById = useMemo(() => new Map(agreements.map((p) => [p.id, p])), [agreements]);

  /** Column a new row's cursor lands in: today, or Monday when today is elsewhere. */
  const todayCol = Math.max(0, week.days.indexOf(today));

  /**
   * Put the cursor on a row's cell. Falls back across the week when today is
   * outside this week, or when today's cell is the read-only running-timer
   * button.
   *
   * It SELECTS the cell, it does not open an editor: the cell is a button, and
   * the next digit typed opens the editor holding that digit. Nothing is ever
   * pre-selected for a keystroke to overwrite.
   */
  const focusRowCell = (rowIndex: number): boolean => {
    for (const col of [todayCol, 0, 1, 2, 3, 4, 5, 6]) {
      const cell = cellRefs.current.get(`${rowIndex}:${col}`);
      if (!cell) continue;
      cell.focus();
      return true;
    }
    return false;
  };

  /**
   * Backstop for the case where no re-render follows the request (picking a
   * row that is already on the sheet). The effect below normally wins and
   * clears the request first, leaving this a no-op.
   */
  const flushPendingFocus = () => {
    requestAnimationFrame(() => {
      const target = pendingFocusRow.current;
      if (target === null) return;
      pendingFocusRow.current = null;
      focusRowCell(target);
    });
  };

  // The tint on a freshly added row is a cue, not a state — it fades on its own.
  useEffect(() => {
    if (!newRowKey) return;
    const id = window.setTimeout(() => setNewRowKey(null), 1800);
    return () => window.clearTimeout(id);
  }, [newRowKey]);

  /**
   * The pace meters under the day totals fill against THIS person's contracted
   * day (weekly capacity ÷ 5 working days) — 8h for full-time staff, 3.2h for a
   * 16h/week contractor. A hardcoded 8 made a contractor's week look empty.
   */
  const capacity = weekCapacity(week.person);
  const dayTarget = capacity.daily;

  // Live timer: elapsed seconds tick into today's cell (and the totals) until
  // the timer stops and the hours are committed to the entry.
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!runningTimer) return;
    setNow(Date.now());
    const interval = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(interval);
  }, [runningTimer]);
  const runningKey = runningTimer ? rowKeyOf(runningTimer.agreementId, runningTimer.taskId) : null;
  const runningElapsedMs = runningTimer ? Math.max(0, now - new Date(runningTimer.startedAt).getTime()) : 0;
  /** Unsaved hours the running timer adds to today's cell (0 when off-week). */
  const runningHours =
    runningTimer && week.days.includes(today)
      ? Math.round((runningElapsedMs / 3_600_000) * 100) / 100
      : 0;
  const liveExtra = (rowKey: string, day: string) =>
    rowKey === runningKey && day === today ? runningHours : 0;

  const rows: GridRow[] = useMemo(() => {
    const serverKeys = new Set(week.rows.map((r) => rowKeyOf(r.agreementId, r.taskId)));
    const server = week.rows.map((row) => ({
      key: rowKeyOf(row.agreementId, row.taskId),
      agreementId: row.agreementId,
      taskId: row.taskId,
      agreement: row.agreement,
      taskName: row.task?.name ?? null,
      billable: row.billable,
      serverRow: row,
    }));
    const local = localRows
      .filter((r) => !serverKeys.has(rowKeyOf(r.agreementId, r.taskId)))
      .flatMap((r) => {
        const agreement = agreementById.get(r.agreementId);
        if (!agreement) return [];
        return [
          {
            key: rowKeyOf(r.agreementId, r.taskId),
            agreementId: r.agreementId,
            taskId: r.taskId,
            agreement,
            taskName: r.taskName,
            billable: r.billable,
            serverRow: null,
          },
        ];
      });
    const all = [...server, ...local];
    // Assignment is idempotent (only unseen keys take a number), so React's
    // double-invoked memo in development produces the same order.
    const { index } = orderRef.current;
    for (const row of all) {
      if (!index.has(row.key)) index.set(row.key, orderRef.current.next++);
    }
    return all.sort((a, b) => (index.get(a.key) ?? 0) - (index.get(b.key) ?? 0));
  }, [week.rows, localRows, agreementById]);

  const existingKeys = useMemo(() => new Set(rows.map((r) => r.key)), [rows]);

  /** Per-row label + which of its three parts is actually the differentiator. */
  const rowLabels = useMemo(() => rows.map((r) => rowLabel(r.agreement, r.taskName)), [rows]);
  const emphases = useMemo(() => rowEmphases(rowLabels), [rowLabels]);

  /**
   * Quick-add suggestions for a thin week.
   *
   * A two-row Monday left half the screen empty with nothing under it and no
   * answer to "what do I usually work on?" — the user had to open a picker and
   * search for combos the server already knows they used last week. Same
   * source as the picker's Recent group, so the two can never disagree.
   */
  const recentRows = useRecentRowKeys(personId);
  const quickAdd = useMemo(() => {
    const out: {
      key: string;
      agreement: TimeAgreement;
      task: TimeTaskOption | null;
      label: RowLabel;
      billable: boolean;
    }[] = [];
    const seen = new Set<string>();
    for (const row of [...(recentRows.data?.recent ?? []), ...(recentRows.data?.rows ?? [])]) {
      const key = rowKeyOf(row.agreement.id, row.taskId);
      if (seen.has(key) || existingKeys.has(key)) continue;
      seen.add(key);
      const agreement = agreementById.get(row.agreement.id);
      if (!agreement) continue;
      const task = row.taskId ? (agreement.tasks ?? []).find((t) => t.id === row.taskId) ?? null : null;
      if (row.taskId && !task) continue;
      out.push({
        key,
        agreement,
        task,
        label: rowLabel(agreement, task?.name ?? row.task?.name ?? null),
        billable: task ? task.billable : agreement.billBy !== 'NONE',
      });
      if (out.length >= 6) break;
    }
    return out;
  }, [recentRows.data, existingKeys, agreementById]);

  /**
   * A row added by the picker (or by "copy last timesheet") only exists in the
   * DOM after this commit — the first moment its cell input can be focused.
   */
  useEffect(() => {
    const target = pendingFocusRow.current;
    if (target === null) return;
    pendingFocusRow.current = null;
    focusRowCell(target);
    // focusRowCell reads refs only; `rows` changing is the signal that matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  /** Rows picked but not yet given hours — nothing about them is stored yet. */
  const unsavedRowCount = useMemo(() => rows.filter((r) => !r.serverRow).length, [rows]);

  /**
   * The running clock knows exactly which row it is on, so its seconds belong
   * on the correct side of the billable split rather than dropping out of both.
   */
  /** No weekend hours anywhere this week (incl. a running timer)? Shrink them. */
  const quietWeekend = useMemo(
    () => week.days.slice(5).every((day) => (week.dayTotals[day] ?? 0) === 0),
    [week.days, week.dayTotals],
  );

  /**
   * Entries with no description are exactly what an invoice line cannot use.
   * Counted only where a note can still be ADDED — a billed, deleted-in-Harvest
   * or under-review entry cannot take one, and a number nobody can bring down
   * is noise. The same test outlines the cells in red, so the count and the
   * outlines always agree.
   */
  const { weekEntryCount, missingNotes } = useMemo(() => {
    const entries = week.rows.flatMap((r) => r.entries);
    return {
      weekEntryCount: entries.length,
      missingNotes: entries.filter((e) => needsNote(e) && !lockOf(e.date)).length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [week.rows, reviews]);

  const runningRowBillable = useMemo(() => {
    if (!runningKey) return true;
    const row = rows.find((r) => r.key === runningKey);
    if (row) return row.billable;
    return runningTimer ? runningTimer.agreement.billBy !== 'NONE' : true;
  }, [rows, runningKey, runningTimer]);

  const addLocalRow = (agreement: TimeAgreement, task: TimeTaskOption | null) => {
    const key = rowKeyOf(agreement.id, task?.id ?? null);
    const existing = rows.findIndex((r) => r.key === key);
    // The pick is only half the action — the cursor has to land in the cell the
    // hours go into, or the keyboard flow dies here and the user reaches for
    // the mouse. Today's column when today is in view, Monday otherwise.
    pendingFocusRow.current = existing >= 0 ? existing : rows.length;
    setNewRowKey(key);
    setPickerOpen(false);
    setLocalRows((prev) =>
      prev.some((r) => rowKeyOf(r.agreementId, r.taskId) === key)
        ? prev
        : [
            ...prev,
            {
              agreementId: agreement.id,
              taskId: task?.id ?? null,
              taskName: task?.name ?? null,
              billable: task ? task.billable : agreement.billBy !== 'NONE',
            },
          ],
    );
    flushPendingFocus();
  };

  /**
   * "Add row" from the keyboard, from ANYWHERE on this screen.
   *
   * This used to be a React `onKeyDown` on the grid Card, which is a bubbling
   * handler: it only ever fired when focus was already inside the Card. On a
   * fresh load (focus on `<body>`), after clicking a week arrow, and — worst —
   * on an empty week, which contains no focusable cell at all, the advertised
   * `A` did nothing while the footer and the button badge both promised it.
   * A document listener fixes the head of the flow the same way the picker's
   * focus hand-off fixed its tail.
   *
   * Guards, in order: never while a picker/note/confirm owns the keyboard,
   * never inside another dialog or listbox, and never while the user is
   * TYPING — "abc" in a text box used to lose its "a" to this shortcut and
   * drop "bc" into the picker's search box.
   *
   * The grid's OWN hours cells are exempt from the typing guard (they carry
   * `data-grid-cell="hours"`). That guard is what killed the shortcut on every
   * populated week: the grid autofocuses today's cell on arrival, so the very
   * first `A` a user pressed was typed into a numeric field that then went red.
   * A letter is never a legal value there, so nothing is lost by claiming it.
   * The cell's own `onKeyDown` normally gets there first and marks the event
   * handled; this is the backstop, not the mechanism.
   */
  // …and never on a week under review, where the row it would add could not be
  // given any hours anyway.
  const shortcutsBlocked =
    pickerOpen || noteTarget !== null || rowToDelete !== null || reviewLocked !== null;
  useEffect(() => {
    if (shortcutsBlocked) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (target?.closest('[role="dialog"], [role="listbox"], [role="menu"]')) return;
      const gridCell = target?.dataset.gridCell === 'hours';
      const typing =
        !gridCell &&
        (target instanceof HTMLInputElement ||
          target instanceof HTMLTextAreaElement ||
          target instanceof HTMLSelectElement ||
          target?.isContentEditable === true);
      const combo = (event.metaKey || event.ctrlKey) && event.key === 'Enter';
      const bare =
        // toLowerCase so Caps Lock still adds a row; Shift+A is excluded below
        // because that is a different keystroke, not the same one shouted.
        event.key.toLowerCase() === 'a' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !event.shiftKey &&
        !typing;
      if (!combo && !bare) return;
      event.preventDefault();
      setPickerOpen(true);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [shortcutsBlocked]);

  /**
   * Where the keyboard starts on this screen: today's cell of the first row,
   * or "Add row" when the week is still empty (there is no cell to aim at, and
   * building the week is the only thing to do). Returns false when neither
   * exists so callers can fall back.
   */
  const focusEntryPoint = (): boolean => {
    if (rows.length > 0 && focusRowCell(0)) return true;
    const button = emptyAddRef.current ?? addRowRef.current;
    if (!button) return false;
    button.focus();
    return true;
  };

  /**
   * The way OUT of the sheet.
   *
   * Escape in a clean cell parks focus on the sheet itself: visible (it takes a
   * ring), still a keyboard context where `A` works, and one Tab from the
   * footer controls. Without it, a keyboard user who lands in the grid has 42
   * tab stops between them and anything else on the page.
   */
  const leaveGrid = () => {
    sheetRef.current?.focus();
  };

  // The page's "Skip to timesheet" link needs the real entry point, not just
  // the card's bounding box. Re-published every render so it always closes over
  // the current rows.
  useEffect(() => {
    if (!focusRef) return;
    focusRef.current = focusEntryPoint;
    return () => {
      focusRef.current = null;
    };
  });

  /** Keep the horizontal-overflow cues honest as rows, notes and size change. */
  useEffect(() => {
    const node = scrollRef.current;
    if (!node) return;
    const measure = () =>
      setOverflow({
        left: node.scrollLeft > 4,
        right: node.scrollLeft + node.clientWidth < node.scrollWidth - 4,
      });
    measure();
    node.addEventListener('scroll', measure, { passive: true });
    window.addEventListener('resize', measure);
    return () => {
      node.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
    };
  }, [rows.length, showNotes, quietWeekend]);

  /**
   * ⚠ **ARRIVAL FOCUS LANDS ON THE SHEET, NEVER IN A CELL.**
   *
   * It used to land in today's cell of the first row — and, because a cell was
   * an input whose contents were selected on focus, the page opened with real
   * billable hours highlighted and a caret in them, with zero user interaction.
   * One stray `9` plus a click anywhere rewrote the week (32.50 → 39.00);
   * Backspace plus a click DELETED an entry, its note and its id outright.
   *
   * The sheet container is focusable, takes a visible ring, is one Tab from the
   * first cell and is a keyboard context where `A` works — so the keyboard flow
   * this effect exists for survives, while no keystroke can reach the billing
   * database until the user has aimed at a cell. "Skip to timesheet" still
   * goes all the way in, because that is somebody asking for it.
   *
   * Only from a cold `<body>` focus: this component is re-keyed per person and
   * per week, so a remount is also what happens on "Next week", and yanking
   * focus off the control someone is still using is worse than the problem.
   */
  useEffect(() => {
    if (!window.matchMedia('(min-width: 768px)').matches) return;
    const active = document.activeElement;
    if (active && active !== document.body && active !== document.documentElement) return;
    sheetRef.current?.focus();
    // Mount only — see above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- saving ---------------------------------------------------------------

  /**
   * ⚠ **UNDO IS A STACK, AND THE SCREEN SAYS SO.**
   *
   * One Ctrl+Z reverts one change — and a change is whatever ONE gesture
   * wrote, so a fill of five cells is one entry here and comes back in one
   * transaction. Pressing it again steps to the change before it, which the
   * bar states in words; it is not idempotent and must not silently look it.
   * The entry is popped only once the server has taken the whole restore, so
   * a refused undo leaves the bar exactly where it was, still offering it.
   */
  const [changes, setChanges] = useState<CellChange[]>([]);
  const [undoing, setUndoing] = useState(false);
  const lastChange = changes.length > 0 ? changes[changes.length - 1] : null;
  /** Deep enough for a session of typing; bounded so the tab cannot grow. */
  const UNDO_DEPTH = 20;
  const pushChange = (change: CellChange) =>
    setChanges((prev) => [...prev, change].slice(-UNDO_DEPTH));

  const cellKey = (rowKey: string, date: string) => `${rowKey}|${date}`;

  const clearFailure = (key: string) =>
    setFailedCells((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Map(prev);
      next.delete(key);
      return next;
    });

  /**
   * ⚠ **EVERY CELL WRITE IS RECORDED SO IT CAN BE PUT BACK.**
   *
   * `Ctrl+Z` used to undo a fill and nothing else: a single cell edit — the
   * commonest write on the screen, and the one that can hard-delete an entry
   * by going to zero — had no inverse at all, and pressing Ctrl+Z after one
   * did nothing and said nothing. Now one mechanism covers both: the bar under
   * the grid names what changed and puts it back, whether that was four cells
   * from `F` or one cell from a keystroke.
   *
   * A restore carries the cell's DESCRIPTION as well as its hours when the cell
   * held exactly one entry, because clearing a cell deletes the entry outright
   * — hours alone would come back as an unlabelled figure.
   *
   * `track: false` is how the undo itself writes, so undoing never becomes the
   * next thing to undo.
   *
   * A failure must never eat billable time either: the typed hours stay in the
   * cell, the cell is marked unsaved (amber) with a retry, and the toast says
   * exactly which day and which row it could not save.
   */
  const commitCell = (
    row: GridRow,
    date: string,
    hours: number,
    options?: { track?: boolean; notes?: string | null },
  ) => {
    const key = cellKey(row.key, date);
    setPreview(null);
    const previous = Math.round((row.serverRow?.dayHours[date] ?? 0) * 100) / 100;
    /**
     * ⚠ Taken BEFORE the write and pushed AFTER it lands. Before, because the
     * rows this replaces are gone the moment the server answers; after, because
     * a bar offering to undo a write that never happened is a second lie — and
     * because only the SERVER can say which entries it removed.
     */
    const snapshot = snapshotCell(row, date);
    const track = options?.track !== false && previous !== Math.round(hours * 100) / 100;
    setCell.mutate(
      {
        agreementId: row.agreementId,
        taskId: row.taskId,
        date,
        hours,
        ...(options?.notes != null ? { notes: options.notes } : {}),
      },
      {
        onSuccess: (result) => {
          clearFailure(key);
          if (!track) return;
          pushChange({
            kind: 'cell',
            message: `${weekdayLabel(date)} ${shortDate(date)} · ${
              rowLabel(row.agreement, row.taskName).full
            } — ${formatHoursValue(previous, hoursFormat)} → ${
              hours > 0 ? formatHoursValue(hours, hoursFormat) : 'cleared'
            }${removedLabel(result.removed)}`,
            restore: [{ row, date, entries: snapshot, after: writtenRows(result.entries) }],
          });
        },
        onError: (error) => {
          const message = error instanceof Error ? error.message : 'Saving failed.';
          setFailedCells((prev) => new Map(prev).set(key, { hours, message }));
          /**
           * ⚠ **THE SERVER'S OWN SENTENCE IS THE POINT OF A REFUSAL.**
           *
           * This used to print a generic "Couldn't save 2.00 h on Mon Aug 17"
           * and drop `message` on the floor — so a cell refused *because
           * typing it would delete two authored entries* said nothing about
           * the entries, and the typist had no idea what to do next. The
           * reason leads; where it happened follows.
           */
          toast.error(
            `${message} (${weekdayLabel(date)} ${shortDate(date)} — ${
              rowLabel(row.agreement, row.taskName).full
            }. Your ${formatHoursValue(
              hours,
              hoursFormat,
            )} is still in the cell; click the amber marker to retry.)`,
          );
        },
      },
    );
  };

  // --- fill right -----------------------------------------------------------

  /**
   * **One gesture for the commonest shape in agency time tracking.**
   *
   * "8.00 on this row Monday to Friday" was five separate cell edits — five
   * focus moves, five commits — against a spreadsheet where it is one drag.
   * That is a direct violation of the constraint this whole product is built
   * to: *fewer steps beats more fidelity*.
   *
   * It fills the remaining **weekdays** of the row, never the weekend: nobody
   * drags 8 h into Saturday, and a fill that did would be a correction, not a
   * shortcut. It is fully undoable, it never writes over an hour it may not
   * (billed, deleted in Harvest, or already equal), and it says in words which
   * days it touched and which it left alone.
   */
  const fillTargets = (fromDate: string): string[] => {
    const from = week.days.indexOf(fromDate);
    if (from < 0) return [];
    // days[0..4] are Mon–Fri (weeks always start Monday). A fill started on the
    // weekend has nothing sensible to spread into.
    // …and never into a day whose period is closed to this viewer.
    return from <= 4 ? week.days.slice(from + 1, 5).filter((d) => lockOf(d) === null) : [];
  };

  /**
   * The last write this grid made, and how to put it back.
   *
   * ⚠ **IT HOLDS ROWS, NOT A TOTAL — this is the whole repair.** A cell can
   * carry several entries with separate notes, separate billable flags and
   * separate invoice lines, and a day total describes none of that. The undo
   * used to replay the total: a cell of 0.50 “Alpha” / 2.00 “Beta” / 1.00
   * “Gamma” typed down to 2.00 and undone came back reading 3.50 with Beta and
   * Gamma DESTROYED and 3.50 h booked under “Alpha”. The figures reconciled
   * perfectly, which is exactly what made it invisible. So a change carries the
   * cell's entries and the undo reinstates precisely those rows.
   */
  /**
   * ⚠ **AND IT RECONCILES WITH WHAT IS THERE NOW — the second half.**
   *
   * `entries` is the cell BEFORE the write (what to put back); `after` is what
   * the write LEFT there, read back from the server's own answer. The pair is
   * what lets the undo tell its own output apart from work logged since — in
   * the cell popover, the day view, the timer, or by an approver in another
   * tab — none of which touches this stack.
   *
   * THE RULE (stated once, enforced on the server in `restoreScope`): a change
   * is **rebased** around a later ADDITION — the new row is kept and the undo
   * still reverts what it was asked to — and **invalidated** by a later EDIT
   * or DELETE of a row it would rewrite, which refuses with the reason and
   * leaves the change on the stack. Silently reverting a cell to a state that
   * predates somebody's later edit is the same defect as replaying a total.
   */
  interface CellChange {
    kind: 'fill' | 'cell';
    message: string;
    restore: Array<PendingRestore & { after: RestoreEntry[] }>;
  }

  /**
   * A cell a write is about to touch, and what it held first. `after` is only
   * knowable once the server has answered, so it is attached at push time.
   */
  type PendingRestore = { row: GridRow; date: string; entries: RestoreEntry[] };

  /** The cell writes of one change, in the shape `POST /time/cells` takes. */
  const restoreCells = (change: CellChange) =>
    change.restore.map((cell) => ({
      agreementId: cell.row.agreementId,
      taskId: cell.row.taskId,
      date: cell.date,
      restore: cell.entries,
      expect: cell.after,
    }));

  /**
   * The rows a write is about to replace — everything in the cell it is allowed
   * to touch. Billed hours, Harvest-deleted hours and a running timer are never
   * removed by a cell write, so they are never part of putting one back either.
   */
  const snapshotCell = (row: GridRow, date: string): RestoreEntry[] =>
    (row.serverRow?.entries ?? [])
      .filter((e) => e.date === date && !e.locked && !e.deletedInSource && !e.running)
      .map((e) => ({ id: e.id, hours: e.hours, notes: e.notes, billable: e.billable }));

  /** How many entries are on this cell that a write could rewrite. */
  const editableEntries = (row: GridRow, date: string) =>
    (row.serverRow?.entries ?? []).filter(
      (e) => e.date === date && !e.locked && !e.deletedInSource && !e.running,
    );

  /**
   * ⚠ **NAME WHAT WENT.** “3.50 → 2.00” was the entire report of two authored
   * entries being destroyed. A removal is now spelled out on the undo bar, with
   * the note that made it a separate piece of work in the first place.
   */
  const removedLabel = (removed: RemovedEntry[] | undefined): string => {
    if (!removed || removed.length === 0) return '';
    const named = removed
      .slice(0, 2)
      .map((e) => (e.notes ? `“${e.notes}”` : formatHoursValue(e.hours, hoursFormat)))
      .join(', ');
    const more = removed.length > 2 ? ` and ${removed.length - 2} more` : '';
    return ` · removed ${removed.length} ${
      removed.length === 1 ? 'entry' : 'entries'
    }: ${named}${more}`;
  };

  /**
   * ⚠ **ONE KEYSTROKE, ONE REQUEST, ONE TRANSACTION.**
   *
   * `F` used to fire one `POST /time/cell` per day in a loop, so a fill could
   * half-land, and the record of what it wrote could be — and was — replaced
   * by the record of the single cell committed a tick later. The whole spread
   * now goes to `POST /time/cells` as one batch: the server writes every cell
   * or none, and the batch is what the undo stack holds.
   *
   * `includeSeed` is `F` pressed inside the editor: the hours being typed have
   * not been written yet, and they belong to THIS gesture, so they ride in the
   * same batch instead of being committed separately.
   */
  const fillRight = async (
    row: GridRow,
    fromDate: string,
    hours: number,
    includeSeed = false,
  ) => {
    if (lockOf(fromDate) || hours <= 0) return;
    const label = rowLabel(row.agreement, row.taskName).full;
    const restore: PendingRestore[] = [];
    const write: Array<{ date: string; hours: number }> = [];
    /** Days the fill SPREAD into — the seed cell is not one of them. */
    const written: string[] = [];
    let refused = 0;
    let already = 0;
    /** Days left alone because filling them would delete an authored entry. */
    let crowded = 0;

    const savedOn = (day: string) =>
      Math.round((row.serverRow?.dayHours[day] ?? 0) * 100) / 100;

    if (includeSeed && savedOn(fromDate) !== hours) {
      restore.push({ row, date: fromDate, entries: snapshotCell(row, fromDate) });
      write.push({ date: fromDate, hours });
    }

    for (const day of fillTargets(fromDate)) {
      const cellEntries = row.serverRow?.entries.filter((e) => e.date === day) ?? [];
      // Immutable = billed, deleted in Harvest, or carrying a running clock.
      // The same three the server treats as a floor, so the arithmetic here and
      // the arithmetic that writes cannot disagree about what a fill may do.
      const immutable = cellEntries.filter((e) => e.locked || e.deletedInSource || e.running);
      const lockedHours = Math.round(immutable.reduce((s, e) => s + e.hours, 0) * 100) / 100;
      const saved = savedOn(day);
      // A cell whose hours are entirely billed or deleted in Harvest is not a
      // cell; and a fill may never take a day BELOW what is already invoiced.
      if (cellEntries.length > 0 && immutable.length === cellEntries.length) {
        refused += 1;
        continue;
      }
      if (hours < lockedHours) {
        refused += 1;
        continue;
      }
      if (saved === hours) {
        already += 1;
        continue;
      }
      /**
       * ⚠ **ONE `F` USED TO DESTROY EVERY MULTI-ENTRY CELL IT LANDED ON.**
       * A day holding “W1” / “W2” / “W3” filled with 1.00 h came back as one
       * entry, and the totals still reconciled. The server refuses that write
       * now, so the fill leaves the day alone and SAYS it did rather than
       * failing the whole batch on one crowded cell.
       */
      const editable = editableEntries(row, day);
      if (editable.length > 1) {
        const rest =
          Math.round(
            editable.slice(0, -1).reduce((sum, e) => sum + e.hours, 0) * 100,
          ) / 100;
        if (Math.round((hours - lockedHours) * 100) / 100 < rest) {
          crowded += 1;
          continue;
        }
      }
      restore.push({ row, date: day, entries: snapshotCell(row, day) });
      write.push({ date: day, hours });
      written.push(day);
    }

    if (write.length === 0) {
      toast.info(
        crowded > 0
          ? `Nothing to fill — ${crowded} day${crowded === 1 ? '' : 's'} hold${
              crowded === 1 ? 's' : ''
            } several entries, and ${formatHoursValue(
              hours,
              hoursFormat,
            )} would delete one. Open a cell (Alt+Enter) to change them.`
          : refused > 0
            ? `Nothing to fill — the rest of the week is billed or locked here.`
            : already > 0
              ? `The rest of the week already reads ${formatHoursValue(hours, hoursFormat)}.`
              : 'Nothing to the right of this day to fill.',
      );
      return;
    }

    setPreview(null);
    let result;
    try {
      result = await setCells.mutateAsync({
        cells: write.map((cell) => ({
          agreementId: row.agreementId,
          taskId: row.taskId,
          date: cell.date,
          hours: cell.hours,
        })),
      });
    } catch (error) {
      // All of them or none of them — the server rolled the batch back, so say
      // that, rather than leaving the user to guess which days landed.
      const message = error instanceof Error ? error.message : 'Saving failed.';
      toast.error(
        `Nothing was filled — ${message} All ${write.length} cell${
          write.length === 1 ? '' : 's'
        } are unchanged.`,
      );
      return;
    }

    const spread = formatHoursValue(hours, hoursFormat);
    const left = refused > 0 ? ` · ${refused} left alone (billed or locked)` : '';
    const held =
      crowded > 0
        ? ` · ${crowded} left alone (several entries — it would delete one)`
        : '';
    // What the write LEFT in each cell, from the server's own answer — the
    // half of the record that lets the undo keep later work instead of
    // flattening it. Keyed by day: a fill is one row across several days.
    const after = new Map(result.cells.map((cell) => [cell.date, writtenRows(cell.entries)]));
    pushChange({
      kind: 'fill',
      message:
        (written.length > 0
          ? `Filled ${weekdayLabel(write[0].date)}–${weekdayLabel(
              written[written.length - 1],
            )} with ${spread} on ${label} · ${write.length} cells${left}${held}`
          : `Set ${weekdayLabel(write[0].date)} to ${spread} on ${label}${left}${held}`) +
        removedLabel(result.cells.flatMap((cell) => cell.removed)),
      restore: restore.map((cell) => ({ ...cell, after: after.get(cell.date) ?? [] })),
    });
  };

  /**
   * **Ctrl+Z / the Undo button — one change, all of its cells, one request.**
   *
   * The restore is a batch for the same reason the fill is: putting four of
   * five cells back is not an undo, it is a fifth state nobody asked for. The
   * change is popped off the stack only after the server has taken all of it,
   * so a refusal leaves the bar in place, still offering the same undo.
   */
  const undoLastChange = async () => {
    const change = changes.length > 0 ? changes[changes.length - 1] : null;
    if (!change || undoing) return;
    const count = change.restore.length;
    /** Rows, not cells — the number that says whether the notes came back. */
    const rows = change.restore.reduce((sum, cell) => sum + cell.entries.length, 0);
    setUndoing(true);
    try {
      const result = await setCells.mutateAsync({ cells: restoreCells(change) });
      setChanges((prev) => prev.slice(0, -1));
      /**
       * ⚠ **AN UNDO THAT COVERED LESS THAN THE WHOLE CELL SAYS SO.** The
       * server keeps rows logged after the change — in the popover, the day
       * view, another tab — instead of flattening them, and the sentence has
       * to state it or the reader is left with a total they cannot account
       * for. It names them, because a note is what makes an entry a separate
       * piece of work.
       */
      const kept = result.cells.flatMap((cell) => cell.kept);
      const keptNote =
        kept.length > 0
          ? ` ${kept.length} ${kept.length === 1 ? 'entry' : 'entries'} logged since ${
              kept.length === 1 ? 'was' : 'were'
            } kept (${kept
              .slice(0, 2)
              .map((e) => (e.notes ? `“${e.notes}”` : formatHoursValue(e.hours, hoursFormat)))
              .join(', ')}${kept.length > 2 ? ` and ${kept.length - 2} more` : ''}).`
          : '';
      toast.info(
        `${change.kind === 'fill' ? 'Fill' : 'Change'} undone — ${count} cell${
          count === 1 ? '' : 's'
        } put back${
          rows > 0 ? `, ${rows} ${rows === 1 ? 'entry' : 'entries'} restored` : ' (cleared)'
        }.${keptNote}`,
      );
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Saving failed.';
      toast.error(
        `Nothing was undone — ${message} ${
          count === 1 ? 'The cell still holds' : `All ${count} cells still hold`
        } what was written; press Ctrl+Z to try again.`,
      );
    } finally {
      setUndoing(false);
    }
  };

  /**
   * **Ctrl/Cmd+Z undoes the last change** — a fill, or a single cell.
   *
   * `F` writes four cells at once — four `POST /api/time/cell` calls into the
   * billing database from one keystroke — and the only way back was a mouse
   * click on a transient "Undo" bar, in a grid that otherwise navigates like a
   * spreadsheet. A destructive shortcut with no keyboard inverse is a trap: the
   * hand that pressed F is still on the keyboard. The same argument applies to
   * a single cell, which is why one hour typed over another is now on the same
   * bar and the same key.
   *
   * It is claimed ONLY while there is something to undo, so an ordinary Ctrl+Z
   * inside a text box (the note popover, the row picker's search) still reaches
   * the browser's own undo. The bar remains, and is still the thing that says
   * what will be put back — and, when more than one change is stacked up, that
   * pressing it again steps back through them.
   */
  useEffect(() => {
    if (changes.length === 0) return;
    const onUndo = (event: globalThis.KeyboardEvent) => {
      if (event.defaultPrevented) return;
      if (event.key.toLowerCase() !== 'z' || event.shiftKey) return;
      if (!(event.metaKey || event.ctrlKey)) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      // A dialog owns its own keyboard; never reach across one.
      if (target?.closest('[role="dialog"], [role="listbox"], [role="menu"]')) return;
      event.preventDefault();
      void undoLastChange();
    };
    document.addEventListener('keydown', onUndo);
    return () => document.removeEventListener('keydown', onUndo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [changes, undoing]);

  /**
   * ⚠ **REMOVING A ROW IS A BATCH WRITE TOO — the same defect, one dialog away.**
   *
   * It fired one `DELETE /api/time/:id` per entry through `Promise.all`, so a
   * refusal on any one of them (a Harvest-deleted entry answers 409) left the
   * others already gone: half a row deleted, the row itself already off the
   * screen, and nothing offering to put any of it back. It is now the same
   * single transaction the fill uses — each day set to what its IMMUTABLE
   * hours come to, which is exactly "delete the unbilled entries and leave the
   * billed ones" — and it goes on the undo stack like every other write.
   */
  const confirmDeleteRow = async () => {
    const row = rowToDelete;
    if (!row) return;
    setLocalRows((rows) => rows.filter((r) => rowKeyOf(r.agreementId, r.taskId) !== row.key));
    const cells: Array<{ date: string; hours: number }> = [];
    const restore: PendingRestore[] = [];
    for (const day of week.days) {
      const cellEntries = row.serverRow?.entries.filter((e) => e.date === day) ?? [];
      if (cellEntries.length === 0) continue;
      // A day in a closed period keeps everything — the server would refuse
      // the whole batch for it.
      if (lockOf(day)) continue;
      const saved = Math.round((row.serverRow?.dayHours[day] ?? 0) * 100) / 100;
      // Billed and Harvest-deleted hours are a floor that survives the removal.
      const keep =
        Math.round(
          cellEntries
            // A running clock survives a row removal too — its elapsed time is
            // recorded nowhere else, and the server refuses to delete it.
            .filter((e) => e.locked || e.deletedInSource || e.running)
            .reduce((s, e) => s + e.hours, 0) * 100,
        ) / 100;
      if (keep === saved) continue;
      cells.push({ date: day, hours: keep });
      restore.push({ row, date: day, entries: snapshotCell(row, day) });
    }
    if (cells.length === 0) return;
    let result;
    try {
      result = await setCells.mutateAsync({
        cells: cells.map((cell) => ({
          agreementId: row.agreementId,
          taskId: row.taskId,
          date: cell.date,
          hours: cell.hours,
        })),
      });
    } catch (error) {
      toast.error(
        `Nothing was removed — ${
          error instanceof Error ? error.message : 'deleting the row failed.'
        } All ${cells.length} day${cells.length === 1 ? '' : 's'} are unchanged.`,
      );
      return;
    }
    const afterRemoval = new Map(
      result.cells.map((cell) => [cell.date, writtenRows(cell.entries)]),
    );
    pushChange({
      kind: 'fill',
      message: `Removed ${rowLabel(row.agreement, row.taskName).full} · ${cells.length} day${
        cells.length === 1 ? '' : 's'
      } cleared`,
      restore: restore.map((cell) => ({ ...cell, after: afterRemoval.get(cell.date) ?? [] })),
    });
  };

  const copyFromLastWeek = async () => {
    setCopyState('loading');
    try {
      const result = await fetchRecentRows(weekStart, personId);
      if (result.rows.length === 0) {
        setCopyState('none');
        return;
      }
      // Computed outside the updater so the focus target can be derived from
      // it — `rows` already covers both server and local rows.
      const added = result.rows
        .filter((r) => !existingKeys.has(rowKeyOf(r.agreement.id, r.taskId)))
        .map((r) => {
          const agreement = agreementById.get(r.agreement.id);
          const taskOption = r.taskId
            ? agreement?.tasks?.find((t) => t.id === r.taskId)
            : undefined;
          return {
            agreementId: r.agreement.id,
            taskId: r.taskId,
            taskName: r.task?.name ?? taskOption?.name ?? null,
            billable: taskOption
              ? taskOption.billable
              : (agreement?.billBy ?? r.agreement.billBy) !== 'NONE',
          };
        });
      if (added.length > 0) {
        // Same contract as "Add row": the copy lands you in the first new row's
        // today cell, ready to type, instead of on `<body>`.
        pendingFocusRow.current = rows.length;
        setNewRowKey(rowKeyOf(added[0].agreementId, added[0].taskId));
        setLocalRows((prev) => [...prev, ...added]);
      }
      setCopyState('idle');
    } catch {
      setCopyState('idle');
      toast.error('Could not load your last timesheet — try again.');
    }
  };

  // --- note popover ---------------------------------------------------------

  const openNote = (target: CellNoteTarget) => {
    noteTargetRef.current = target;
    setNoteTarget(target);
  };

  const closeNote = () => {
    const previous = noteTargetRef.current;
    noteTargetRef.current = null;
    setNoteTarget(null);
    if (!previous) return;
    // Put the user back where they were — unless the click that closed this
    // popover opened another cell's.
    requestAnimationFrame(() => {
      if (noteTargetRef.current) return;
      cellRefs.current.get(`${previous.row}:${previous.col}`)?.focus();
    });
  };

  const noteRow = noteTarget ? rows.find((r) => r.key === noteTarget.rowKey) ?? null : null;

  // --- preview (uncommitted typing feeding every total) ---------------------

  const setCellPreview = (row: GridRow, date: string, saved: number, hours: number | null) => {
    if (hours === null || Math.round((hours - saved) * 100) === 0) {
      setPreview((prev) => (prev && prev.rowKey === row.key && prev.date === date ? null : prev));
      return;
    }
    setPreview({
      rowKey: row.key,
      date,
      delta: Math.round((hours - saved) * 100) / 100,
      billable: row.billable,
    });
  };
  const rowPreview = (key: string) => (preview?.rowKey === key ? preview.delta : 0);
  const dayPreview = (day: string) => (preview?.date === day ? preview.delta : 0);
  const weekPreview = preview?.delta ?? 0;

  const isEmpty = rows.length === 0;
  /**
   * How far this week is from the contracted total.
   *
   * The ZERO case gets a full empty state with a primary action; a two-row,
   * 13-of-40 week used to get nothing at all below the card and a tertiary
   * footer link — a weaker treatment for the bigger problem. Materially short
   * (a quarter of the week or more still missing) turns the recents strip into
   * an actual prompt with "Copy last timesheet" as its primary action.
   */
  const shortfall =
    capacity.weekly === null
      ? null
      : Math.round((capacity.weekly - (week.weekTotal + runningHours)) * 100) / 100;
  const wellShort =
    !isEmpty && shortfall !== null && capacity.weekly !== null && shortfall > capacity.weekly * 0.25;
  /**
   * The strip below the sheet — recent rows to click, plus a shortfall call to
   * action once the week has started. An EMPTY week still gets the recents
   * (that strip is part of its empty state); only the "still to log" prompt is
   * withheld there, because the empty state above already carries the ask.
   */
  const showFillPrompt =
    !reviewLocked && rows.length < 6 && (quickAdd.length > 0 || wellShort);
  /** True while that strip owns "Copy last timesheet" as its primary action. */
  const promptOwnsCopy = showFillPrompt && wellShort;
  /**
   * Sticky columns keep row identity on screen while days scroll. The TOTAL
   * column is only sticky from md up: at 390px two sticky columns leave ~110px
   * for seven days and the total lands on top of Tuesday.
   */
  const stickyLeft =
    // Below xl the label column gives width back to the days: at 1024px a
    // 320px label column pushed Fri/Sat/Sun off-screen, and "the week at a
    // glance" is this screen's whole promise. Client name truncates instead.
    // 10rem at 390px, not 11: with the identity now clipping its shared
    // context instead of wrapping it, the label column can give a third day
    // column back to the week.
    'sticky left-0 z-20 w-[10rem] sm:w-48 xl:w-80 ' +
    "after:pointer-events-none after:absolute after:inset-y-0 after:-right-3 after:w-3 after:content-[''] " +
    'after:bg-gradient-to-r after:from-black/[0.04] after:to-transparent';
  /**
   * Day column widths. Below xl every pixel is contested: at 1024 the seven
   * days plus a 320px label column ran 106px past the viewport and Fri–Sun
   * left the screen. Monday carries the month, so it gets a little more.
   */
  const dayWidth = (colIndex: number) =>
    colIndex === 0
      ? 'w-[64px] xl:w-[88px]'
      : colIndex >= 5 && quietWeekend
        ? 'w-[54px] xl:w-[58px]'
        : 'w-[64px] xl:w-[80px]';
  const stickyRight =
    'w-[4.25rem] sm:w-[5.25rem] md:sticky md:right-0 md:z-20 ' +
    "md:before:pointer-events-none md:before:absolute md:before:inset-y-0 md:before:-left-3 md:before:w-3 md:before:content-[''] " +
    'md:before:bg-gradient-to-l md:before:from-black/[0.04] md:before:to-transparent';

  return (
    <>
      <WeekSummary
        week={week}
        runningHours={runningHours}
        runningBillable={runningRowBillable}
        previewDelta={weekPreview}
        previewBillable={preview?.billable ?? true}
        unsavedCells={failedCells.size}
        hoursFormat={hoursFormat}
        onOpenDay={onOpenDay}
      />

      <Card className={cn(foreignSheet && FOREIGN_SHEET_TINT)}>
        {/* Focusable so Escape has somewhere to land: a keyboard user can step
            out of the cells without tabbing through all 42 of them. */}
        <div
          ref={sheetRef}
          tabIndex={-1}
          aria-label="Weekly timesheet grid"
          className="relative rounded-t-xl outline-none focus-visible:ring-2 focus-visible:ring-accent/40"
        >
        {/* Edge cues for the days that do not fit. Rendered OUTSIDE the scroll
            container so they stay pinned to the card's edges instead of
            sliding away with the content they are describing. Only below md,
            where the Total column stops being sticky and the week really does
            run off the side. */}
        {overflow.left && (
          <div
            className="pointer-events-none absolute inset-y-0 left-[10rem] z-30 w-6 bg-gradient-to-r from-white to-transparent md:hidden"
            aria-hidden
          />
        )}
        {overflow.right && (
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-30 flex w-12 items-center justify-end bg-gradient-to-l from-white via-white/90 to-transparent md:hidden"
            aria-hidden
          >
            <span className="mr-1 flex h-6 w-6 items-center justify-center rounded-full border border-grey-200 bg-white text-grey-500 shadow-card">
              <ChevronRight className="h-3.5 w-3.5" />
            </span>
          </div>
        )}
        {/* `relative` so absolutely-positioned sr-only spans inside the table
            anchor here and get clipped — otherwise they stretch the page. */}
        <div ref={scrollRef} className="relative overflow-x-auto">
          {/* table-fixed so the label column cannot swallow the day columns:
              the widths declared in the header row are the widths you get. */}
          <table
            className="w-full min-w-[700px] table-fixed border-separate border-spacing-0 text-sm"
            onMouseLeave={() => setHoverCol(null)}
          >
            <thead>
              <tr className="text-left">
                <th
                  scope="col"
                  className={cn(
                    stickyLeft,
                    'border-b border-grey-200 bg-grey-50 px-2 py-2 text-caption font-bold uppercase tracking-wider text-grey-500 sm:px-4',
                  )}
                >
                  Client / Agreement
                </th>
                {week.days.map((day, colIndex) => (
                  <th
                    key={day}
                    scope="col"
                    onMouseEnter={() => setHoverCol(colIndex)}
                    title={fullDate(day)}
                    className={cn(
                      'whitespace-nowrap border-b border-grey-200 bg-grey-50 px-1 py-1.5 text-right xl:px-2',
                      // A weekend nobody worked does not deserve two of the nine
                      // columns — the space goes back to the agreement names.
                      dayWidth(colIndex),
                      day === today && 'bg-accent/[0.06]',
                      hoverCol === colIndex && 'bg-accent/[0.09]',
                    )}
                  >
                    {/* One line, not two — the header used to cost 57px before
                        a single row of data. The month only appears where
                        there is room for it (Monday, and any day that opens a
                        new month, at xl); everywhere else the page title names
                        the range and the column's tooltip gives the full date. */}
                    <span
                      className={cn(
                        'block text-caption font-bold uppercase tracking-normal xl:tracking-wider',
                        day === today ? 'text-accent-700' : 'text-grey-500',
                      )}
                    >
                      {weekdayLabel(day)}{' '}
                      <span
                        className={cn(
                          'num font-semibold',
                          day === today ? 'text-accent-700' : 'text-grey-400',
                        )}
                      >
                        {colIndex === 0 || day.endsWith('-01') ? (
                          <>
                            <span className="hidden xl:inline">{shortDate(day)}</span>
                            <span className="xl:hidden">{day.slice(8)}</span>
                          </>
                        ) : (
                          day.slice(8)
                        )}
                      </span>
                    </span>
                  </th>
                ))}
                <th
                  scope="col"
                  className={cn(
                    stickyRight,
                    'border-b border-grey-200 bg-grey-50 px-2 py-2 text-right text-caption font-bold uppercase tracking-wider text-grey-500 sm:px-3',
                  )}
                >
                  Total
                </th>
              </tr>
            </thead>
            <tbody>
              {isEmpty ? (
                // Keep the 7-day scaffold: you are about to type into it, so the
                // columns, today's highlight and the totals row must stay put.
                <tr>
                  <td colSpan={9} className="border-b border-grey-100 px-4 py-10 text-center">
                    <Clock className="mx-auto h-7 w-7 text-grey-300" aria-hidden />
                    <p className="mt-2 text-heading-4 text-secondary">No time tracked this week</p>
                    <p className="mx-auto mt-1 max-w-md text-body-sm text-grey-500">
                      Pick an agreement and start typing hours into the day columns — or bring back
                      the rows from your most recent timesheet.
                    </p>
                    <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
                      {/* A first row is the only thing to do here, so the empty
                          state carries the primary action itself rather than
                          pointing at a ghost button in the footer — and it
                          gives an empty week a focusable target at all. Not on
                          a week under review: the server refuses to submit an
                          empty week, so this pairing is only reachable after a
                          reopen-then-clear, and offering "Add the first row"
                          there would be an action that cannot succeed. */}
                      {reviewLocked ? null : (
                      <Button
                        ref={emptyAddRef}
                        size="sm"
                        title="Pick an agreement and task — or just press A"
                        onClick={() => setPickerOpen(true)}
                      >
                        <Plus className="h-4 w-4" aria-hidden />
                        Add the first row
                        <kbd className="hidden rounded border border-white/30 bg-white/10 px-1 text-[10px] font-bold leading-4 sm:inline">
                          A
                        </kbd>
                      </Button>
                      )}
                      {reviewLocked ? (
                        <p className="text-body-sm text-grey-500">{reviewLocked}</p>
                      ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={copyFromLastWeek}
                        loading={copyState === 'loading'}
                        disabled={copyState === 'none'}
                      >
                        <Copy className="h-4 w-4" aria-hidden />
                        {copyState === 'none'
                          ? 'No previous timesheet'
                          : 'Copy rows from last timesheet'}
                      </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row, rowIndex) => {
                  const timerOnRow =
                    runningTimer?.agreementId === row.agreementId &&
                    (runningTimer?.taskId ?? null) === row.taskId;
                  const rowHasUnlocked = (row.serverRow?.entries ?? []).some((e) => !e.locked);
                  const rowFullyLocked =
                    (row.serverRow?.entries.length ?? 0) > 0 && !rowHasUnlocked;
                  const label = rowLabels[rowIndex] ?? rowLabel(row.agreement, row.taskName);
                  const emphasis = emphases[rowIndex] ?? 'agreement';
                  const noteEntries = showNotes
                    ? [...(row.serverRow?.entries ?? [])].sort(
                        (a, b) => a.date.localeCompare(b.date) || a.id.localeCompare(b.id),
                      )
                    : [];
                  return (
                    <Fragment key={row.key}>
                    <tr className="group">
                      <td
                        className={cn(
                          stickyLeft,
                          'border-b border-grey-100 px-2 py-1 group-hover:bg-grey-50 sm:px-4',
                          // The cursor is already in this row's cell; the tint
                          // says which row it just appeared in.
                          row.key === newRowKey ? 'bg-accent/[0.07]' : 'bg-white',
                        )}
                      >
                        <div className="flex items-center gap-2">
                          {/* A week under review takes no new hours, so a timer
                              button on it would only produce a 409. The slot is
                              kept (not collapsed) so every row's text stays on
                              the same left edge as an unlocked week's. */}
                          {reviewLocked ? (
                            // ⚠ TWO LOCKS, TWO GLYPHS. This is the REVIEW lock
                            // (its owner can undo it in a click) and it wears
                            // the week's own status glyph — the same one the
                            // status pill above the grid shows. `Lock` is
                            // reserved for the BILLING lock, which is money and
                            // is not undone from this screen. Two identical
                            // padlocks 40px apart meaning opposite things is
                            // exactly what the coherence rule forbids.
                            <span
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-grey-100 text-grey-300 sm:h-7 sm:w-7"
                              title={reviewLocked}
                            >
                              <ReviewLockIcon className="h-3 w-3" aria-hidden />
                            </span>
                          ) : foreignSheet && !timerOnRow ? (
                            // A timer measures the person at the keyboard. On
                            // somebody else's sheet there is nothing to start —
                            // the slot is kept so every row's text keeps the
                            // same left edge.
                            <span
                              className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-grey-100 text-grey-200 sm:h-7 sm:w-7"
                              title={`${week.person.name} starts their own timer — this is their timesheet, not yours.`}
                            >
                              <Play className="h-3 w-3 translate-x-px fill-current" aria-hidden />
                            </span>
                          ) : (
                          <button
                            type="button"
                            // Tab is reserved for the day cells (Harvest-style):
                            // these controls stay mouse/pointer-driven.
                            tabIndex={-1}
                            disabled={timerBusy}
                            onClick={() =>
                              timerOnRow ? onStopTimer() : onStartTimer(row.agreementId, row.taskId)
                            }
                            title={
                              timerOnRow ? 'Stop timer' : `Start timer for ${label.full} (logs to today)`
                            }
                            className={cn(
                              'flex h-6 w-6 shrink-0 items-center justify-center rounded-full border transition-all sm:h-7 sm:w-7',
                              timerOnRow
                                ? 'border-primary bg-primary text-white hover:bg-primary-600'
                                : 'border-grey-200 bg-white text-grey-400 hover:border-primary/40 hover:bg-primary/5 hover:text-primary',
                            )}
                          >
                            {timerOnRow ? (
                              <Square className="h-3 w-3 fill-current" aria-hidden />
                            ) : (
                              <Play className="h-3 w-3 translate-x-px fill-current" aria-hidden />
                            )}
                            <span className="sr-only">{timerOnRow ? 'Stop timer' : 'Start timer'}</span>
                          </button>
                          )}
                          <RowIdentity label={label} billable={row.billable} emphasis={emphasis} />
                          {reviewLocked ? null : rowFullyLocked ? (
                            <span
                              className="shrink-0 text-grey-300"
                              title="Billed on an invoice — locked until the invoice is voided"
                            >
                              <Lock className="h-3.5 w-3.5" aria-label="All entries billed" />
                            </span>
                          ) : (
                            <button
                              type="button"
                              tabIndex={-1}
                              onClick={() => setRowToDelete(row)}
                              title="Remove row"
                              className="shrink-0 rounded-md p-0.5 text-grey-300 opacity-0 transition-all hover:bg-grey-100 hover:text-primary group-hover:opacity-100 max-md:opacity-100 sm:p-1"
                            >
                              <Trash2 className="h-3.5 w-3.5" aria-hidden />
                              <span className="sr-only">Remove row</span>
                            </button>
                          )}
                        </div>
                      </td>
                      {week.days.map((day, colIndex) => {
                        const cellEntries =
                          row.serverRow?.entries.filter((e) => e.date === day) ?? [];
                        // Immutable = billed here, or deleted in Harvest. The
                        // grid must not let either be typed over: one would
                        // contradict an issued invoice, the other would invent
                        // hours on a record its source of truth deleted.
                        const immutable = cellEntries.filter(
                          (e) => e.locked || e.deletedInSource,
                        );
                        const lockedHours =
                          Math.round(immutable.reduce((s, e) => s + e.hours, 0) * 100) / 100;
                        const fullyLocked =
                          cellEntries.length > 0 && immutable.length === cellEntries.length;
                        const hasDeletedInSource = immutable.some((e) => e.deletedInSource);
                        const saved = row.serverRow?.dayHours[day] ?? 0;
                        const failed = failedCells.get(cellKey(row.key, day)) ?? null;
                        const missingNote =
                          !lockOf(day) && cellEntries.some((e) => needsNote(e));
                        return (
                          <td
                            key={day}
                            onMouseEnter={() => setHoverCol(colIndex)}
                            onFocusCapture={() => setHoverCol(colIndex)}
                            className={cn(
                              'group/cell relative border-b border-grey-100 px-1 py-1',
                              day === today && 'bg-accent/[0.03]',
                              // A closed day inside an otherwise open week reads
                              // as a different state, not as an empty cell.
                              !reviewLocked && lockOf(day) && 'bg-grey-50',
                              hoverCol === colIndex && 'bg-accent/[0.05]',
                            )}
                          >
                            {/* The fill handle — a spreadsheet's drag, as one
                                click. Quiet until the pointer is on the cell,
                                and never offered on a cell with nothing to
                                spread, on the weekend, or on a locked week. */}
                            {!lockOf(day) &&
                              saved > 0 &&
                              fillTargets(day).length > 0 &&
                              !(timerOnRow && day === today) && (
                                <button
                                  type="button"
                                  tabIndex={-1}
                                  onMouseDown={(event) => event.preventDefault()}
                                  onClick={() => void fillRight(row, day, saved)}
                                  title={`Fill ${weekdayLabel(
                                    fillTargets(day)[0],
                                  )}–${weekdayLabel(
                                    fillTargets(day)[fillTargets(day).length - 1],
                                  )} with ${formatHoursValue(saved, hoursFormat)} (or press F)`}
                                  className="absolute -bottom-0.5 right-0 z-10 flex h-4 w-4 items-center justify-center rounded border border-accent bg-white text-accent opacity-0 shadow-sm transition-opacity hover:bg-accent hover:text-white group-hover/cell:opacity-100"
                                >
                                  <ChevronsRight className="h-3 w-3" aria-hidden />
                                  <span className="sr-only">
                                    Fill the rest of the week with these hours
                                  </span>
                                </button>
                              )}
                            {timerOnRow && day === today ? (
                              <RunningCell
                                savedHours={saved}
                                elapsedMs={runningElapsedMs}
                                onStop={onStopTimer}
                                disabled={timerBusy}
                              />
                            ) : (
                              <CellInput
                                row={rowIndex}
                                col={colIndex}
                                rowCount={rows.length}
                                value={saved}
                                entries={cellEntries}
                                missingNote={missingNote}
                                lockedHours={lockedHours}
                                fullyLocked={fullyLocked}
                                hasDeletedInSource={hasDeletedInSource}
                                reviewLocked={lockOf(day)}
                                isToday={day === today}
                                refs={cellRefs}
                                hoursFormat={hoursFormat}
                                failed={failed}
                                ariaLabel={`Hours — ${label.full}, ${weekdayLabel(day)} ${shortDate(day)}`}
                                onCommit={(hours) => commitCell(row, day, hours)}
                                onRetry={() => failed && commitCell(row, day, failed.hours)}
                                onPreview={(hours) => setCellPreview(row, day, saved, hours)}
                                onAddRow={() => setPickerOpen(true)}
                                onFill={(hours, includeThisCell) =>
                                  void fillRight(row, day, hours, includeThisCell)
                                }
                                canFill={!lockOf(day) && fillTargets(day).length > 0}
                                onLeaveGrid={leaveGrid}
                                onOpenNote={(draftHours, anchor) =>
                                  openNote({
                                    rowKey: row.key,
                                    row: rowIndex,
                                    col: colIndex,
                                    date: day,
                                    draftHours,
                                    anchor,
                                  })
                                }
                              />
                            )}
                          </td>
                        );
                      })}
                      <td
                        className={cn(
                          stickyRight,
                          'border-b border-grey-100 bg-white px-2 py-1 text-right font-semibold text-secondary num-cell group-hover:bg-grey-50 sm:px-3',
                          rowPreview(row.key) !== 0 && 'text-accent',
                        )}
                      >
                        {(() => {
                          const live = liveExtra(row.key, today);
                          const total =
                            (row.serverRow?.total ?? 0) + live + rowPreview(row.key);
                          // A running timer's hours are part of this row's week,
                          // so the total must never read "—" while the clock ticks.
                          const running = timerOnRow && week.days.includes(today);
                          const text =
                            total > 0 || running ? formatHoursValue(total, hoursFormat) : '—';
                          if (!running) return text;
                          // The running state rides on the dot, never on the
                          // numeral: red digits in financial software mean
                          // negative, and this figure is climbing.
                          return (
                            <span
                              className="flex items-center justify-end gap-1.5"
                              title="A timer is running on this row — this total is still climbing."
                            >
                              <span className="relative flex h-1.5 w-1.5 shrink-0" aria-hidden>
                                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-60" />
                                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-primary" />
                              </span>
                              {text}
                            </span>
                          );
                        })()}
                      </td>
                    </tr>
                    {/* Notes density row: the entry descriptions this app bills
                        on, printed under the row instead of hidden in tooltips. */}
                    {showNotes && noteEntries.length > 0 && (
                      <tr className="bg-grey-50/70">
                        <td
                          colSpan={week.days.length + 2}
                          className="border-b border-grey-100 px-2 py-1 sm:pl-[3.4rem] sm:pr-4"
                        >
                          <ul className="space-y-px">
                            {noteEntries.map((entry) => {
                              const col = week.days.indexOf(entry.date);
                              const noteText = entry.notes?.trim() ?? '';
                              const missing = needsNote(entry) && !lockOf(entry.date);
                              return (
                                <li key={entry.id}>
                                  <button
                                    type="button"
                                    tabIndex={-1}
                                    // Reading stays; writing does not. On a week
                                    // under review the row is still printed —
                                    // it just no longer opens an editor that
                                    // the server would refuse.
                                    disabled={lockOf(entry.date) !== null}
                                    onClick={(event) =>
                                      openNote({
                                        rowKey: row.key,
                                        row: rowIndex,
                                        col: Math.max(0, col),
                                        date: entry.date,
                                        draftHours: null,
                                        anchor: event.currentTarget,
                                      })
                                    }
                                    title={
                                      lockOf(entry.date)
                                        ? (lockOf(entry.date) ?? undefined)
                                        : noteText
                                          ? 'Edit this note'
                                          : 'This entry has no description — click to add one'
                                    }
                                    className={cn(
                                      'flex w-full items-baseline gap-2 rounded px-1 py-0.5 text-left transition-colors',
                                      lockOf(entry.date) ? 'cursor-default' : 'hover:bg-white',
                                      missing && 'bg-error/5 ring-1 ring-inset ring-error/50 hover:bg-error/10',
                                    )}
                                  >
                                    <span className="w-7 shrink-0 text-caption font-bold uppercase tracking-wide text-grey-400">
                                      {weekdayLabel(entry.date)}
                                    </span>
                                    <span className="w-10 shrink-0 text-right text-caption font-semibold text-grey-500 num">
                                      {formatHoursValue(entry.hours, hoursFormat)}
                                    </span>
                                    <span className="flex w-3 shrink-0 justify-center">
                                      {entry.locked ? (
                                        <Lock
                                          className="h-3 w-3 translate-y-0.5 text-grey-400"
                                          aria-label="Billed"
                                        />
                                      ) : (
                                        <span
                                          className={cn(
                                            'h-1.5 w-1.5 translate-y-px rounded-full',
                                            entry.billable ? 'bg-success' : 'bg-grey-300',
                                          )}
                                          aria-hidden
                                        />
                                      )}
                                    </span>
                                    <span
                                      className={cn(
                                        'min-w-0 flex-1 text-caption leading-snug',
                                        noteText
                                          ? 'text-grey-600'
                                          : missing
                                            ? 'font-semibold text-status-late'
                                            : 'italic text-grey-400',
                                      )}
                                    >
                                      {noteText || 'No description — click to add one'}
                                    </span>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })
              )}
              {/* The picker lands INSIDE the grid, at the bottom, where the row
                  it creates will appear — it used to float over rows 4–6 and
                  hide the very context you were adding into. */}
              {pickerOpen && (
                <tr>
                  <td
                    colSpan={week.days.length + 2}
                    className="border-b border-grey-100 bg-grey-50/70 px-2 py-2 sm:px-4"
                  >
                    <div className="w-full max-w-sm">
                      <AgreementTaskPicker
                        inline
                        dismissable
                        revealOnOpen
                        agreements={agreements}
                        personId={personId}
                        excludeKeys={existingKeys}
                        onPick={addLocalRow}
                        onClose={() => setPickerOpen(false)}
                        triggerRef={addRowRef}
                      />
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
            <tfoot>
              <tr>
                <td
                  className={cn(
                    stickyLeft,
                    'border-t border-grey-200 bg-grey-50 px-2 py-2 align-top sm:px-4',
                  )}
                >
                  <span className="block text-caption font-bold uppercase tracking-wider text-grey-500">
                    Total
                  </span>
                  {/* Names the threshold the day meters below are drawn against
                      — otherwise the green/blue underline is a mystery. */}
                  {dayTarget !== null && (
                    <span className="mt-0.5 block text-caption leading-tight text-grey-400">
                      bars fill to <span className="num">{capacity.dailyLabel}</span>/day
                    </span>
                  )}
                </td>
                {week.days.map((day, colIndex) => {
                  const running = day === today ? runningHours : 0;
                  const total = (week.dayTotals[day] ?? 0) + running + dayPreview(day);
                  const pace =
                    dayTarget === null ? 0 : Math.min(1, Math.max(0, total / dayTarget));
                  return (
                    <td
                      key={day}
                      onMouseEnter={() => setHoverCol(colIndex)}
                      className={cn(
                        'border-t border-grey-200 bg-grey-50 px-2 py-2 text-right align-top',
                        day === today && 'bg-accent/[0.06]',
                        hoverCol === colIndex && 'bg-accent/[0.09]',
                      )}
                    >
                      <span
                        className={cn(
                          'block text-sm font-semibold text-secondary num-cell',
                          day === today && 'text-accent-700',
                          dayPreview(day) !== 0 && 'text-accent',
                        )}
                      >
                        {total || running > 0 ? formatHoursValue(total, hoursFormat) : '—'}
                      </span>
                      {/* Pace against this person's contracted day, not a fixed 8h */}
                      {dayTarget !== null && (
                        <span
                          className="mt-1 block h-0.5 w-full overflow-hidden rounded-full bg-grey-200"
                          title={`${formatHoursValue(total, hoursFormat)} of ${
                            week.person.name
                          }'s ${capacity.dailyLabel} day`}
                          aria-hidden
                        >
                          <span
                            className={cn(
                              'block h-0.5 rounded-full',
                              total >= dayTarget ? 'bg-success' : 'bg-accent/60',
                            )}
                            style={{ width: `${pace * 100}%` }}
                          />
                        </span>
                      )}
                    </td>
                  );
                })}
                <td
                  className={cn(
                    stickyRight,
                    'border-t border-grey-200 bg-grey-50 px-2 py-2 text-right align-top text-sm font-black text-secondary num-cell sm:px-3',
                    weekPreview !== 0 && 'text-accent',
                  )}
                >
                  {formatHoursValue(week.weekTotal + runningHours + weekPreview, hoursFormat)}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
        </div>

        {/* A write to a billing database has to be reversible in one click and
            it has to say what it did — a toast that vanishes in four seconds is
            not where you report hours changing. It stays until it is used or
            dismissed, exactly like the money screens' undo bar. */}
        {lastChange && (
          <div
            data-undo-bar
            className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-t border-accent/30 bg-accent/[0.06] px-4 py-2"
          >
            <p
              data-undo-message
              className="min-w-0 flex-1 text-caption font-semibold text-secondary"
            >
              {lastChange.message}
              {/* ⚠ REPEATED Ctrl+Z STACKS — and the reader is told, because the
                  alternative is a key that quietly does something different
                  the second time. The bar stays until every change on the
                  stack has been undone or the reader dismisses it. */}
              {changes.length > 1 && (
                <span className="ml-2 font-normal text-grey-500">
                  Ctrl+Z again steps back through {changes.length - 1} earlier change
                  {changes.length - 1 === 1 ? '' : 's'}.
                </span>
              )}
            </p>
            <div className="flex items-center gap-1">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => void undoLastChange()}
                disabled={undoing}
                title={
                  lastChange.restore.length > 1
                    ? `Put all ${lastChange.restore.length} cells back (Ctrl+Z)`
                    : 'Undo this change (Ctrl+Z)'
                }
              >
                <Undo2 className="h-3.5 w-3.5" aria-hidden />
                {undoing ? 'Undoing…' : 'Undo'}
                <span className="hidden font-normal text-grey-400 sm:inline">Ctrl+Z</span>
              </Button>
              <button
                type="button"
                onClick={() => setChanges([])}
                aria-label="Dismiss"
                className="rounded-md p-1 text-grey-400 transition-colors hover:bg-grey-100 hover:text-secondary"
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </div>
          </div>
        )}

        <div className="relative flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-grey-100 px-4 py-2.5">
          {/* Every write affordance leaves a week under review, replaced by one
              sentence saying why. Buttons that are present but always fail are
              how a user learns to distrust a screen. */}
          {reviewLocked && (
            <p className="flex items-center gap-1.5 text-caption font-semibold text-grey-500">
              <ReviewLockIcon className="h-3.5 w-3.5 shrink-0" aria-hidden />
              {reviews.get(week.days[0])?.status === 'APPROVED'
                ? 'Approved — read-only until it is reopened'
                : 'Submitted — read-only until it is withdrawn'}
            </p>
          )}
          {/* Half a week closed: name the period and the days, so a greyed
              Monday next to an open Tuesday is not a mystery. */}
          {!reviewLocked &&
            lockedPeriods.map(({ review, days }) => {
              const Glyph = review.status === 'APPROVED' ? CheckCircle2 : Send;
              const first = days[0];
              const last = days[days.length - 1];
              return (
                <p
                  key={`${review.periodKind}:${review.periodStart}`}
                  className="flex items-center gap-1.5 text-caption font-semibold text-grey-500"
                >
                  <Glyph className="h-3.5 w-3.5 shrink-0" aria-hidden />
                  {review.label} {review.status === 'APPROVED' ? 'approved' : 'submitted'} ·{' '}
                  {first === last
                    ? `${weekdayLabel(first)} ${shortDate(first)} is read-only`
                    : `${weekdayLabel(first)}–${weekdayLabel(last)} are read-only`}
                </p>
              );
            })}
          {correctingDays.length > 0 && (
            <p className="flex items-center gap-1.5 text-caption font-semibold text-accent-700">
              <UserCog className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Correcting a submitted period as an admin
              {correctingDays.length < week.days.length &&
                ` · ${weekdayLabel(correctingDays[0])}–${weekdayLabel(
                  correctingDays[correctingDays.length - 1],
                )}`}
            </p>
          )}
          {/* ⚠ THE LEGEND FOR THE TWO LOCKS. They are 40px apart on this grid
              and mean opposite things — one is undone in a click by the person
              who applied it, the other only by voiding an invoice. The glyphs
              now differ; this says what they are, so nobody has to hover nine
              rows to find out. */}
          {hasBilledHours && (
            <p className="flex items-center gap-1.5 text-caption text-grey-400">
              <Lock className="h-3.5 w-3.5 shrink-0 text-grey-300" aria-hidden />
              <span>
                <span className="font-semibold text-grey-500">Billed</span> — on an invoice, locked
                until it is voided
                {reviewLocked && ' (that outlives the review lock)'}
              </span>
            </p>
          )}
          {!reviewLocked && (
          <Button
            ref={addRowRef}
            variant="ghost"
            size="sm"
            title="Add an agreement/task row — or press A anywhere on this screen, including inside an hours cell"
            aria-expanded={pickerOpen}
            onClick={() => setPickerOpen((o) => !o)}
          >
            <Plus className="h-4 w-4" aria-hidden />
            Add row
            <kbd className="hidden rounded border border-grey-200 bg-grey-50 px-1 text-[10px] font-bold leading-4 text-grey-500 sm:inline">
              A
            </kbd>
          </Button>
          )}
          {/* Was only offered on a completely empty week — the common Monday
              case (one row already logged) needed it just as much. The empty
              state carries its own, larger copy of this. */}
          {/* Only one copy of this action on screen: when the week is short the
              prompt strip below carries it as its primary button instead. */}
          {!reviewLocked && !isEmpty && !promptOwnsCopy && (
            <Button
              variant="ghost"
              size="sm"
              onClick={copyFromLastWeek}
              loading={copyState === 'loading'}
              disabled={copyState === 'none'}
              title="Bring back the agreement/task rows from your most recent timesheet (no hours)"
            >
              <Copy className="h-4 w-4" aria-hidden />
              {copyState === 'none' ? 'No previous timesheet' : 'Copy last timesheet'}
            </Button>
          )}
          <Button
            variant={showNotes ? 'outline' : 'ghost'}
            size="sm"
            aria-pressed={showNotes}
            onClick={() => setShowNotes((open) => !open)}
            title={
              missingNotes > 0
                ? `${missingNotes} of ${weekEntryCount} entries this week ${
                    missingNotes === 1 ? 'has' : 'have'
                  } no description — outlined in red. Click to print every description under its row.`
                : "Print every entry's description under its row."
            }
          >
            <MessageSquareText className="h-4 w-4" aria-hidden />
            Notes
            {/* ⚠ A bare "1" beside "Notes" read as one note, or one unread.
                It counts entries with NO description, so it says so. */}
            {missingNotes > 0 && (
              <span className="rounded-full bg-error/10 px-1.5 py-px text-[10px] font-bold text-status-late num">
                {missingNotes} missing
              </span>
            )}
          </Button>
          {/* The shortcut legend describes keys that do nothing on a read-only
              week — advertising them there is exactly the bug this wave came
              to fix, just pointed at a different state. */}
          {/* ⚠ THE LEGEND MUST NOT CONTRADICT ITSELF. It read "F fills the week
              · Esc exits", which is two instructions that cancel out: Esc parks
              focus on the sheet container, and F only works from INSIDE a cell,
              so following the legend left to right did nothing at all and
              fired zero requests. `A` genuinely is global (a document listener
              backs it); `F` and `Alt+Enter` are the cell's own keys, so the
              legend now says which is which instead of listing five keys as if
              they all worked from the same place. */}
          {/* ⚠ **A LABEL, NOT A LESSON** (Jesse, 2026-08-13). Six shortcuts
              printed as a sentence under the grid was `hidden xl:block`, so it
              was clutter on a desktop and ABSENT on every narrower screen —
              the worst of both. Behind the (i) it is one word wide, it renders
              at 390px too, and every key still says which place it works from. */}
          {!reviewLocked && (
            <span className="flex items-center gap-1 text-caption text-grey-400">
              Keyboard
              <InfoHint label="Keyboard" width={300}>
                <dl className="space-y-1.5">
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 font-semibold text-grey-500">A</dt>
                    <dd>adds a row (from anywhere on the sheet)</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 font-semibold text-grey-500">Ctrl+Z</dt>
                    <dd>undoes the last change</dd>
                  </div>
                </dl>
                <p className="mt-2 font-semibold text-grey-500">In a cell</p>
                <dl className="mt-1 space-y-1.5">
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 font-semibold text-grey-500">Enter</dt>
                    <dd>commits</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 font-semibold text-grey-500">F</dt>
                    <dd>fills the rest of the week</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 font-semibold text-grey-500">Alt+Enter</dt>
                    <dd>a note</dd>
                  </div>
                  <div className="flex gap-2">
                    <dt className="w-20 shrink-0 font-semibold text-grey-500">Esc</dt>
                    <dd>leaves the cell</dd>
                  </div>
                </dl>
              </InfoHint>
            </span>
          )}
          <div className="ml-auto flex items-center gap-3">
            {/* A display preference belongs beside the numbers it formats, not
                in the page header competing with the week title. */}
            <SegmentedControl
              ariaLabel="Hours format"
              value={hoursFormat}
              onChange={onHoursFormatChange}
              options={[
                { value: 'decimal', label: <span className="num">1.50</span> },
                { value: 'hm', label: <span className="num">1:30</span> },
              ]}
            />
            {setCell.isPending || setCells.isPending ? (
              <span className="flex items-center gap-1.5 text-caption text-grey-500">
                <Loader2 className="h-3 w-3 animate-spin" aria-hidden /> Saving…
              </span>
            ) : failedCells.size > 0 ? (
              <span className="flex items-center gap-1.5 text-caption font-semibold text-status-partial">
                <AlertTriangle className="h-3 w-3" aria-hidden />
                {failedCells.size} unsaved
              </span>
            ) : unsavedRowCount > 0 ? (
              // A row with no hours has nothing to store, so it vanishes on
              // reload. Saying "Saved" with a green check while that is true
              // is a lie the user only discovers after losing the row.
              <span
                className="flex items-center gap-1.5 text-caption font-semibold text-grey-500"
                title="A row is only kept once it has hours. Type into a day cell (or remove the row) — an empty row is gone on reload."
              >
                <Clock className="h-3 w-3 text-grey-400" aria-hidden />
                {unsavedRowCount} row{unsavedRowCount > 1 ? 's' : ''} not yet saved
              </span>
            ) : (
              <span className="flex items-center gap-1.5 text-caption text-grey-400">
                <Check className="h-3 w-3 text-success" aria-hidden /> Saved
              </span>
            )}
          </div>
        </div>
      </Card>

      {/* A thin week used to end here, with half the viewport below it holding
          nothing and no answer to "what do I normally work on?". These are the
          combos this person actually used recently (server-side, same source as
          the picker's Recent group) — one click puts the row on the sheet with
          the cursor already in today's cell. */}
      {showFillPrompt && (
        <Card className="mt-3">
          <div className="px-4 py-3">
            <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
              <p className="flex flex-wrap items-baseline gap-x-2">
                {wellShort ? (
                  <span className="flex items-center gap-1.5 text-caption font-bold uppercase tracking-wider text-status-partial">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0 translate-y-px" aria-hidden />
                    <span className="num">{formatHoursValue(shortfall ?? 0, hoursFormat)}</span>{' '}
                    still to log this week
                  </span>
                ) : (
                  <span className="flex items-center gap-1.5 text-caption font-bold uppercase tracking-wider text-grey-500">
                    <Clock3 className="h-3.5 w-3.5 shrink-0 translate-y-px" aria-hidden />
                    Pick up where you left off
                  </span>
                )}
                <span className="text-caption text-grey-400">
                  {wellShort
                    ? 'bring back last week’s rows, or add one you tracked recently'
                    : 'rows you tracked recently — one click adds it to this week'}
                </span>
              </p>
              {wellShort && (
                // Primary, not a tertiary footer link: rebuilding the week from
                // the last timesheet is the fastest route out of a thin week.
                <Button
                  size="sm"
                  className="ml-auto"
                  onClick={copyFromLastWeek}
                  loading={copyState === 'loading'}
                  disabled={copyState === 'none'}
                  title="Bring back the agreement/task rows from your most recent timesheet (no hours)"
                >
                  <Copy className="h-4 w-4" aria-hidden />
                  {copyState === 'none' ? 'No previous timesheet' : 'Copy last timesheet'}
                </Button>
              )}
            </div>
            <div className={cn('flex flex-wrap gap-1.5', quickAdd.length > 0 && 'mt-2')}>
              {quickAdd.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  title={`Add ${item.label.full} to this week`}
                  onClick={() => {
                    rememberPick(item.agreement.id, item.task?.id ?? null);
                    addLocalRow(item.agreement, item.task);
                  }}
                  className="group/chip flex max-w-full items-center gap-2 rounded-lg border border-grey-200 bg-white px-2.5 py-1.5 text-left transition-colors hover:border-accent/50 hover:bg-accent/5"
                >
                  <Plus className="h-3.5 w-3.5 shrink-0 text-grey-400 transition-colors group-hover/chip:text-accent" aria-hidden />
                  <span className="min-w-0">
                    <span className="flex min-w-0 items-center gap-1.5">
                      <span
                        className={cn(
                          'h-1.5 w-1.5 shrink-0 rounded-full',
                          item.billable ? 'bg-success' : 'bg-grey-300',
                        )}
                        aria-hidden
                      />
                      <span className="truncate text-caption font-bold text-secondary">
                        {item.label.agreement}
                      </span>
                      {item.label.task && (
                        <span className="shrink-0 truncate rounded bg-grey-100 px-1 py-px text-[10px] font-semibold text-grey-600">
                          {item.label.task}
                        </span>
                      )}
                    </span>
                    <span className="block truncate pl-3 text-caption leading-tight text-grey-500">
                      {item.label.client}
                    </span>
                  </span>
                </button>
              ))}
            </div>
          </div>
        </Card>
      )}

      {noteTarget && noteRow && (
        <CellNotePopover
          key={`${noteTarget.rowKey}|${noteTarget.date}`}
          target={noteTarget}
          label={rowLabel(noteRow.agreement, noteRow.taskName)}
          agreementId={noteRow.agreementId}
          taskId={noteRow.taskId}
          rowBillable={noteRow.billable}
          entries={noteRow.serverRow?.entries.filter((e) => e.date === noteTarget.date) ?? []}
          hoursFormat={hoursFormat}
          weekStart={weekStart}
          personId={personId}
          agreements={agreements}
          onClose={closeNote}
          onOpenDay={(date) => {
            closeNote();
            onOpenDay(date);
          }}
        />
      )}

      <ConfirmDialog
        open={rowToDelete !== null}
        onOpenChange={(open) => !open && setRowToDelete(null)}
        title="Remove this row?"
        destructive
        confirmLabel="Remove row"
        description={
          rowToDelete
            ? rowToDelete.serverRow && rowToDelete.serverRow.total > 0
              ? `This deletes ${formatHoursValue(
                  // Exactly what `confirmDeleteRow` removes: not billed, not
                  // deleted in Harvest, not a running clock, and not on a day
                  // whose period is closed — the dialog must not promise more.
                  rowToDelete.serverRow.entries
                    .filter(
                      (e) => !e.locked && !e.deletedInSource && !e.running && !lockOf(e.date),
                    )
                    .reduce((s, e) => s + e.hours, 0),
                  hoursFormat,
                )} unbilled hours on ${
                  rowLabel(rowToDelete.agreement, rowToDelete.taskName).full
                } this week. Billed entries and closed days stay.`
              : `${
                  rowLabel(rowToDelete.agreement, rowToDelete.taskName).full
                } has no hours this week — the row is just removed.`
            : undefined
        }
        onConfirm={confirmDeleteRow}
      />
    </>
  );
}
